import React, { useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import VatContext from './Contexts/VatContext';
import { useCurrency } from './Contexts/CurrencyContext';
import ReportExport from './ReportExport';
import {
  formatDateForInput as formatDateForInputValue,
  formatDisplayDate,
  getDateLocaleForCountry,
  parseFlexibleDateValue
} from './lib/dateUtils';
import { getTransactionVatAmount as getTransactionVatAmountForReporting } from './lib/vatReportingUtils';
import {
  buildLatestVatFiledSnapshotMap,
  buildVatCarryForwardAdjustments,
  buildVatFiledReturnAdjustments,
  calculateVatFiledNetForTransactions,
  getVatCarryForwardAdjustmentSnapshotAmount,
  getVatFiledPeriodNetAmount,
  getVatFiledPeriodSnapshotNetAmount,
  sanitizeVatFiledPeriods,
  summarizeVatCarryForwardAdjustments
} from './lib/vatFilingUtils';
import { getBusinessTypeLabelForCountry, getCountryTerminology } from './lib/countrySettings';

const BUSINESS_TYPES = Object.freeze({
  SOLE_TRADER: 'sole_trader',
  LIMITED_COMPANY: 'limited_company'
});

const normalizeBusinessType = (value) => (
  value === BUSINESS_TYPES.LIMITED_COMPANY
    ? BUSINESS_TYPES.LIMITED_COMPANY
    : BUSINESS_TYPES.SOLE_TRADER
);

const getCorporationTaxSettings = (taxJurisdiction) => {
  switch (taxJurisdiction) {
    case 'Ireland':
      return {
        jurisdiction: 'Ireland',
        taxYearLabel: '2026',
        label: 'Irish Corporation Tax',
        rate: 0.125,
        note: 'Uses the 12.5% Irish trading income rate. Passive or non-trading company income can be taxed at 25%.'
      };
    case 'United Kingdom':
      return {
        jurisdiction: 'United Kingdom',
        taxYearLabel: '2026/27',
        label: 'UK Corporation Tax',
        smallProfitsRate: 0.19,
        mainRate: 0.25,
        lowerLimit: 50000,
        upperLimit: 250000,
        marginalReliefFraction: 3 / 200,
        note: 'Uses UK 2026/27 non-ring-fence rates. Associated companies and ring-fence profits are not modelled.'
      };
    case 'United States':
      return {
        jurisdiction: 'United States',
        taxYearLabel: '2026',
        label: 'US Federal Corporation Tax',
        rate: 0.21,
        note: 'Uses the federal C corporation rate only. State corporate income taxes are not included.'
      };
    default:
      return {
        jurisdiction: taxJurisdiction || 'Selected jurisdiction',
        taxYearLabel: '2026',
        label: `${taxJurisdiction || 'Selected Jurisdiction'} Corporation Tax`,
        rate: 0.25,
        note: 'Uses a simplified 25% corporation tax estimate because this jurisdiction is not configured.'
      };
  }
};

const calculateCorporationTax = (profit, taxJurisdiction) => {
  const settings = getCorporationTaxSettings(taxJurisdiction);
  const rawProfit = Number(profit) || 0;
  const taxableProfit = Math.max(0, rawProfit);
  let corporationTax = 0;
  let rateLabel = '';
  let method = 'flat';
  const breakdown = [];

  if (settings.jurisdiction === 'United Kingdom') {
    const {
      smallProfitsRate,
      mainRate,
      lowerLimit,
      upperLimit,
      marginalReliefFraction
    } = settings;

    if (taxableProfit <= 0) {
      rateLabel = 'No corporation tax on zero or negative profit';
    } else if (taxableProfit <= lowerLimit) {
      corporationTax = taxableProfit * smallProfitsRate;
      rateLabel = `${(smallProfitsRate * 100).toFixed(0)}% small profits rate`;
      breakdown.push({
        label: `${(smallProfitsRate * 100).toFixed(0)}% on taxable profit`,
        amount: corporationTax
      });
    } else if (taxableProfit >= upperLimit) {
      corporationTax = taxableProfit * mainRate;
      rateLabel = `${(mainRate * 100).toFixed(0)}% main rate`;
      breakdown.push({
        label: `${(mainRate * 100).toFixed(0)}% on taxable profit`,
        amount: corporationTax
      });
    } else {
      method = 'uk_marginal_relief';
      const mainRateTax = taxableProfit * mainRate;
      const marginalRelief = (upperLimit - taxableProfit) * marginalReliefFraction;
      corporationTax = Math.max(0, mainRateTax - marginalRelief);
      rateLabel = `${(mainRate * 100).toFixed(0)}% main rate less Marginal Relief`;
      breakdown.push(
        {
          label: `${(mainRate * 100).toFixed(0)}% main rate tax`,
          amount: mainRateTax
        },
        {
          label: 'Marginal Relief',
          amount: -marginalRelief
        }
      );
    }
  } else {
    const rate = Number(settings.rate) || 0;
    corporationTax = taxableProfit * rate;
    const ratePercent = rate * 100;
    rateLabel = `${ratePercent.toFixed(ratePercent % 1 === 0 ? 0 : 1)}%`;
    if (taxableProfit > 0) {
      breakdown.push({
        label: `${rateLabel} on taxable profit`,
        amount: corporationTax
      });
    }
  }

  const roundedTax = parseFloat(corporationTax.toFixed(2));

  return {
    calculationType: 'corporation_tax',
    jurisdiction: settings.jurisdiction,
    taxLabel: settings.label,
    taxYearLabel: settings.taxYearLabel,
    taxableProfit: parseFloat(taxableProfit.toFixed(2)),
    corporationTax: roundedTax,
    totalTaxLiability: roundedTax,
    profitAfterTax: parseFloat((rawProfit - roundedTax).toFixed(2)),
    effectiveTaxRate: taxableProfit > 0 ? roundedTax / taxableProfit : 0,
    rateLabel,
    method,
    breakdown: breakdown.map((entry) => ({
      ...entry,
      amount: parseFloat((Number(entry.amount) || 0).toFixed(2))
    })),
    note: settings.note,
    settings
  };
};

const getTransactionYear = (transaction) => {
  const rawDate = transaction?.date || transaction?.purchaseDate || transaction?.invoiceDate || transaction?.createdAt;
  const parsed = rawDate ? new Date(rawDate) : null;
  const year = parsed && !Number.isNaN(parsed.getTime()) ? parsed.getFullYear() : null;
  return Number.isFinite(year) ? year : null;
};

const getCapitalAssetCost = (transaction) => {
  const amount = Number(
    transaction?.expenseTotalAmount ??
    transaction?.billTotalAmount ??
    transaction?.invoiceTotalAmount ??
    transaction?.originalAmount ??
    transaction?.amount
  );
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

const calculateCapitalAllowanceSummary = ({
  assets = [],
  isCapitalExpense,
  taxYear
}) => {
  const normalizedTaxYear = Number(taxYear) || new Date().getFullYear();
  const capitalAllowanceYears = 8;
  const annualAllowanceRate = 0.125;

  const rows = (Array.isArray(assets) ? assets : [])
    .filter((asset) => typeof isCapitalExpense === 'function' && isCapitalExpense(asset?.category))
    .map((asset) => {
      const cost = getCapitalAssetCost(asset);
      const purchaseYear = getTransactionYear(asset);
      if (!cost || !purchaseYear) return null;

      const depreciationYears = Math.max(1, Number(asset?.depreciationYears) || 8);
      const disposalYear = getTransactionYear({ date: asset?.disposalDate });
      const isDisposedBeforeTaxYear = disposalYear && disposalYear < normalizedTaxYear;
      const isInBookDepreciationPeriod =
        normalizedTaxYear >= purchaseYear &&
        normalizedTaxYear < purchaseYear + depreciationYears &&
        !isDisposedBeforeTaxYear;
      const isInCapitalAllowancePeriod =
        normalizedTaxYear >= purchaseYear &&
        normalizedTaxYear < purchaseYear + capitalAllowanceYears &&
        !isDisposedBeforeTaxYear;

      const bookDepreciation = isInBookDepreciationPeriod ? cost / depreciationYears : 0;
      const capitalAllowance = isInCapitalAllowancePeriod ? cost * annualAllowanceRate : 0;

      return {
        id: asset.id || `${asset.description || asset.category}-${purchaseYear}-${cost}`,
        description: asset.description || asset.category || 'Capital asset',
        cost: parseFloat(cost.toFixed(2)),
        purchaseYear,
        depreciationYears,
        bookDepreciation: parseFloat(bookDepreciation.toFixed(2)),
        capitalAllowance: parseFloat(capitalAllowance.toFixed(2))
      };
    })
    .filter(Boolean);

  const bookDepreciationAddBack = rows.reduce((sum, row) => sum + row.bookDepreciation, 0);
  const capitalAllowances = rows.reduce((sum, row) => sum + row.capitalAllowance, 0);

  return {
    taxYear: normalizedTaxYear,
    annualAllowanceRate,
    capitalAllowanceYears,
    bookDepreciationAddBack: parseFloat(bookDepreciationAddBack.toFixed(2)),
    capitalAllowances: parseFloat(capitalAllowances.toFixed(2)),
    rows
  };
};

// Tax Calculation Component for handling Income Tax, Corporation Tax, Capital Gains Tax, and VAT
function TaxCalculation({
  transactions,
  allTransactions,
  updatedTransactions,
  totals,
  adjustedNetProfit,
  isCapitalExpense,
  categories = { income: [], expenses: [] },
  buildTransactionVatEntries,
  user,
  handleGoogleSignIn,
  countryName = 'Ireland', // Default country name
  businessType = BUSINESS_TYPES.SOLE_TRADER,
  taxYear = new Date().getFullYear(),
  className = '', // For additional styling
  onTaxSummaryChange = () => {},
  openVatSectionSignal = 0,
  rctTracker = {},
  supportsRct = false,
  categoryVatRateRows = []
}) {
  // Get formatCurrency from CurrencyContext
  const { formatCurrency } = useCurrency();
  // State for collapsible section
  const [isExpanded, setIsExpanded] = React.useState(true);
  const [showIncomeTax, setShowIncomeTax] = React.useState(false);
  const [showCapitalGains, setShowCapitalGains] = React.useState(false);
  const [showRct, setShowRct] = React.useState(false);
  const [selectedRctSection, setSelectedRctSection] = React.useState('');
  const [showVat, setShowVat] = React.useState(false);
  const vatSectionRef = React.useRef(null);
  // Use the provided country name as the tax jurisdiction
  const taxJurisdiction = countryName;
  const countryTerminology = React.useMemo(
    () => getCountryTerminology(taxJurisdiction),
    [taxJurisdiction]
  );
  const taxName = countryTerminology.taxName;
  const normalizedBusinessType = normalizeBusinessType(businessType);
  const isLimitedCompany = normalizedBusinessType === BUSINESS_TYPES.LIMITED_COMPANY;
  const businessStructureLabel = React.useMemo(
    () => getBusinessTypeLabelForCountry(normalizedBusinessType, taxJurisdiction),
    [normalizedBusinessType, taxJurisdiction]
  );
  const dateDisplayLocale = React.useMemo(
    () => getDateLocaleForCountry(countryName || taxJurisdiction),
    [countryName, taxJurisdiction]
  );
  
  // State for US filing status and state selection
  const [usFilingStatus, setUsFilingStatus] = React.useState('single');
  const [usState, setUsState] = React.useState('NY');
  
  // Get VAT settings from context at component level
  const vatContextValue = React.useContext(VatContext);
  const defaultVatSettings = React.useMemo(() => {
    const isUk = taxJurisdiction === 'United Kingdom';
    const isUs = taxJurisdiction === 'United States';
    return {
      enabled: false,
      isRegistered: false,
      registrationDate: '',
      registrationPeriods: [],
      accountingBasis: 'cash',
      productsRate: isUs ? 0 : (isUk ? 0.2 : 0.23),
      servicesRate: isUs ? 0 : (isUk ? 0.2 : 0.135),
      salesRate: isUs ? 0 : (isUk ? 0.2 : 0.23),
      categoryVatRates: {},
      vatSettlementOffset: 0,
      vatFiledPeriods: []
    };
  }, [taxJurisdiction]);
  const vatSettings = vatContextValue
    ? {
        enabled: Boolean(vatContextValue.enabled),
        isRegistered: Boolean(vatContextValue.isRegistered),
        registrationDate: vatContextValue.registrationDate || '',
        registrationPeriods: Array.isArray(vatContextValue.registrationPeriods)
          ? vatContextValue.registrationPeriods
          : [],
        productsRate: typeof vatContextValue.productsRate === 'number' ? vatContextValue.productsRate : defaultVatSettings.productsRate,
        servicesRate: typeof vatContextValue.servicesRate === 'number' ? vatContextValue.servicesRate : defaultVatSettings.servicesRate,
        salesRate: typeof vatContextValue.salesRate === 'number'
          ? vatContextValue.salesRate
          : (typeof vatContextValue.productsRate === 'number' ? vatContextValue.productsRate : defaultVatSettings.salesRate),
        categoryVatRates: vatContextValue.categoryVatRates && typeof vatContextValue.categoryVatRates === 'object'
          ? vatContextValue.categoryVatRates
          : defaultVatSettings.categoryVatRates,
        accountingBasis: vatContextValue.accountingBasis === 'invoice' ? 'invoice' : 'cash',
        vatSettlementOffset: Number.isFinite(Number(vatContextValue.vatSettlementOffset))
          ? Number(vatContextValue.vatSettlementOffset)
          : defaultVatSettings.vatSettlementOffset,
        vatFiledPeriods: Array.isArray(vatContextValue.vatFiledPeriods)
          ? vatContextValue.vatFiledPeriods
          : defaultVatSettings.vatFiledPeriods
      }
    : defaultVatSettings;
  const syncVatSettings = vatContextValue?.setVatSettings;
  
  // Update US tax settings when filing status or state changes
  React.useEffect(() => {
    if (taxJurisdiction === 'United States') {
      console.log(`US tax settings updated: Filing status: ${usFilingStatus}, State: ${usState}`);
    }
  }, [taxJurisdiction, usFilingStatus, usState]);

  React.useEffect(() => {
    if (!openVatSectionSignal) return;
    setIsExpanded(true);
    setShowVat(true);

    const timer = setTimeout(() => {
      try {
        vatSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch {}
    }, 80);

    return () => clearTimeout(timer);
  }, [openVatSectionSignal]);
  
  // Tax rates and thresholds by jurisdiction (updated for 2026 where available)
  const taxRates = React.useMemo(() => {
    switch(taxJurisdiction) {
      case 'United States':
        return {
          cgt: {
            shortTermRate: 0.37, // Highest marginal rate (simplified)
            longTermRate: 0.20, // Maximum long-term rate
            shortTermLabel: 'Short-term (<=1 year)',
            longTermLabel: 'Long-term (>1 year)'
          },
          income: {
            // 2026 standard deduction (single filer only for simplicity)
            standardDeduction: {
              single: 16100
            },
            // Assume single filer only
            filingStatus: 'single',
            brackets: {
              // 2026 brackets (single filer only)
              // Threshold represents the upper bound of the bracket
              single: [
                { threshold: 12400, rate: 0.10 },
                { threshold: 50400, rate: 0.12 },
                { threshold: 105700, rate: 0.22 },
                { threshold: 201775, rate: 0.24 },
                { threshold: 256225, rate: 0.32 },
                { threshold: 640600, rate: 0.35 },
                { threshold: Number.POSITIVE_INFINITY, rate: 0.37 }
              ]
            },
            selfEmploymentTax: {
              socialSecurityRate: 0.124, // 12.4% (employer + employee portions)
              medicareRate: 0.029, // 2.9% (employer + employee portions)
              socialSecurityWageCap: 168600 // 2025 wage base limit (projected)
            },
            additionalMedicareRate: 0.009, // 0.9% additional Medicare tax
            additionalMedicareThreshold: 200000, // Threshold for additional Medicare tax
            // State income tax rates (2025 projected)
            stateTax: {
              // State income tax rates (flat rate states)
              flatRateStates: {
                CO: 0.0455, IL: 0.0495, IN: 0.0323, KY: 0.045, MA: 0.05, MI: 0.0425, 
                NC: 0.0475, NH: 0.04, PA: 0.0307, UT: 0.0485, WA: 0.00
              },
              // Progressive tax states - simplified with highest marginal rate
              progressiveRateStates: {
                AL: 0.05, AZ: 0.045, AR: 0.055, CA: 0.133, CT: 0.0699, DE: 0.066, GA: 0.0575,
                HI: 0.11, ID: 0.06, IA: 0.0625, KS: 0.057, LA: 0.0425, ME: 0.0715, MD: 0.0575,
                MN: 0.0985, MS: 0.05, MO: 0.054, MT: 0.0675, NE: 0.0664, NJ: 0.1075, NM: 0.059,
                NY: 0.109, ND: 0.0290, OH: 0.0399, OK: 0.0475, OR: 0.099, RI: 0.0599,
                SC: 0.07, VT: 0.0875, VA: 0.0575, WV: 0.065, WI: 0.0765, DC: 0.0995
              },
              // No income tax states
              noTaxStates: ['AK', 'FL', 'NV', 'SD', 'TN', 'TX', 'WY'],
              // State standard deductions (simplified)
              standardDeductions: {
                CA: 5202, GA: 5400, VA: 4500, OR: 2420, NY: 8000,
                // Default for other states
                default: 2000
              }
            },
            // Sales tax rates (for VAT/Sales Tax section)
            salesTax: {
              AL: 0.04, AK: 0.00, AZ: 0.056, AR: 0.065, CA: 0.0725, CO: 0.029, CT: 0.0635, DE: 0.00,
              FL: 0.06, GA: 0.04, HI: 0.04, ID: 0.06, IL: 0.0625, IN: 0.07, IA: 0.06, KS: 0.065,
              KY: 0.06, LA: 0.0445, ME: 0.055, MD: 0.06, MA: 0.0625, MI: 0.06, MN: 0.06875, MS: 0.07,
              MO: 0.04225, MT: 0.00, NE: 0.055, NV: 0.0685, NH: 0.00, NJ: 0.06625, NM: 0.05125, NY: 0.04,
              NC: 0.0475, ND: 0.05, OH: 0.0575, OK: 0.045, OR: 0.00, PA: 0.06, RI: 0.07, SC: 0.06,
              SD: 0.045, TN: 0.07, TX: 0.0625, UT: 0.0485, VT: 0.06, VA: 0.053, WA: 0.065, WV: 0.06,
              WI: 0.05, WY: 0.04, DC: 0.06
            },
            selectedState: usState // Use the selected state
          }
        };
      case 'United Kingdom':
        return {
          cgt: {
            basicRate: 0.10, // Basic rate taxpayers
            higherRate: 0.20, // Higher rate taxpayers
            allowance: 3000, // Annual exempt amount (2026/27 frozen)
            label: 'Capital Gains Tax'
          },
          income: {
            taxYearLabel: '2026/27',
            personalAllowance: 12570, // 2026/27 tax year (frozen)
            basicRateThreshold: 50270, // 20% band upper limit (frozen)
            higherRateThreshold: 125140, // 40% to 45% taper point (frozen)
            basicRate: 0.20,
            higherRate: 0.40,
            additionalRate: 0.45,
            personalAllowanceTaperThreshold: 100000, // Personal allowance starts to taper at £100,000
            personalAllowanceTaperRate: 0.5, // Reduces by £1 for every £2 over threshold
            // National Insurance Contributions (Class 4) for 2026/27 (assumed unchanged)
            ni: {
              lowerProfitsThreshold: 12570,
              upperProfitsThreshold: 50270,
              lowerProfitsRate: 0.06, // 6% on profits between lower and upper threshold
              upperProfitsRate: 0.02 // 2% on profits above upper threshold
            }
          }
        };
      case 'France':
        return {
          cgt: {
            rate: 0.30, // Flat tax rate (PFU - Prelevement Forfaitaire Unique)
            label: 'Impot sur les Plus-Values'
          },
          income: {
            brackets: [
              { threshold: 0, rate: 0.0 },
              { threshold: 11497, rate: 0.11 },
              { threshold: 29315, rate: 0.30 },
              { threshold: 83823, rate: 0.41 },
              { threshold: 180294, rate: 0.45 }
            ],
            socialCharges: 0.172, // CSG, CRDS, etc. (17.2%)
            standardDeduction: 10, // 10% standard deduction for employment income
            maxStandardDeduction: 13522 // Maximum standard deduction amount
          }
        };
      case 'Germany':
        return {
          cgt: {
            rate: 0.25, // 25% flat rate plus solidarity surcharge
            solidaritySurcharge: 0.055, // 5.5% of the tax amount
            churchTax: 0.08, // 8-9% depending on the state (using 8% as default)
            exemption: 801, // Annual exemption for investment income
            label: 'Kapitalertragsteuer'
          },
          income: {
            basicAllowance: 12096, // Basic tax-free allowance (Grundfreibetrag) for 2025
            brackets: [
              { threshold: 0, rate: 0.0 },
              { threshold: 12096, rate: 0.14 }, // Starting rate
              { threshold: 68429, rate: 0.42 }, // Higher rate
              { threshold: 277825, rate: 0.45 } // Top rate
            ],
            solidaritySurcharge: 0.055, // 5.5% of income tax
            solidaritySurchargeThreshold: 16956, // Threshold for solidarity surcharge
            churchTax: 0.08, // 8-9% depending on the state (using 8% as default)
            // Municipal trade tax (Gewerbesteuer) for sole traders
            tradeTax: {
              allowance: 24500, // Tax-free allowance
              baseRate: 0.035, // Base rate of 3.5%
              // Municipality multipliers (Hebesatz)
              municipalityMultipliers: {
                "Berlin": 410,
                "Hamburg": 470,
                "Frankfurt am Main": 460,
                "Cologne": 475,
                "Stuttgart": 420,
                "Dusseldorf": 440,
                "Leipzig": 460,
                "Dortmund": 485,
                "Essen": 480,
                "Grunwald": 240,
                "Liebenwalde": 250,
                "Wackersdorf": 350,
                "Michelau i.OFr.": 400,
                "Beuron": 350
              },
              // Default municipality (Berlin)
              defaultMunicipality: "Berlin"
            }
          }
        };
      case 'Italy':
        return {
          cgt: {
            rate: 0.26, // 26% for most capital gains
            realEstateRate: 0.21, // 21% for real estate capital gains
            label: 'Imposta sulle Plusvalenze'
          },
          income: {
            // Standard IRPEF rates for 2025
            brackets: [
              { threshold: 0, rate: 0.23 },
              { threshold: 28000, rate: 0.35 },
              { threshold: 50000, rate: 0.43 }
            ],
            // Flat-rate regime (Regime Forfettario)
            flatRate: {
              firstFiveYears: 0.05, // 5% for first 5 years
              afterFiveYears: 0.15, // 15% after 5 years
              eligibleThreshold: 85000 // Maximum eligible income
            },
            regionalTax: 0.0173, // Regional tax (varies by region, using average)
            municipalTax: 0.008, // Municipal tax (varies by municipality, using average)
            deductions: {
              employment: 1880, // Employment deduction
              dependent: 950, // Dependent family members deduction
              medical: 0.19 // 19% of qualifying medical expenses
            }
          }
        };
      case 'Spain':
        return {
          cgt: {
            brackets: [
              { threshold: 0, rate: 0.19 },
              { threshold: 6000, rate: 0.21 },
              { threshold: 50000, rate: 0.23 },
              { threshold: 200000, rate: 0.26 },
              { threshold: 300000, rate: 0.27 }
            ],
            label: 'Impuesto sobre Ganancias de Capital'
          },
          income: {
            // 2025 combined state and regional rates
            stateBrackets: [
              { threshold: 0, rate: 0.19 },
              { threshold: 12450, rate: 0.24 },
              { threshold: 20200, rate: 0.30 },
              { threshold: 35200, rate: 0.37 },
              { threshold: 60000, rate: 0.45 },
              { threshold: 300000, rate: 0.49 }
            ],
            personalAllowance: 5550, // Personal allowance
            employmentAllowance: {
              base: 2000, // Base employment income allowance
              maxIncome: 16825, // Maximum income for full allowance
              minAllowance: 0 // Minimum allowance for high incomes
            },
            // Social Security Contributions (RETA) for 2025
            socialSecurity: {
              rate: 0.314, // 31.4% general contribution rate
              minMonthlyBase: 1323, // Minimum monthly base
              maxMonthlyBase: 4909.50, // Maximum monthly base
              // Solidarity contribution for high incomes
              solidarityContributions: [
                { threshold: 4909.50, rate: 0.0092 }, // 0.92% for income between €4,909.50 and €5,400.45
                { threshold: 5400.46, rate: 0.01 },   // 1% for income between €5,400.46 and €7,364.25
                { threshold: 7364.25, rate: 0.0117 }  // 1.17% for income above €7,364.25
              ]
            },
            // VAT (IVA) rates
            vat: {
              standardRate: 0.21, // 21% standard rate
              reducedRates: [0.10, 0.04] // 10% and 4% reduced rates
            }
          }
        };
      case 'Ireland':
      default:
        return {
          cgt: {
            rate: 0.33, // 33% CGT rate
            annualExemption: 1270, // Annual exemption amount
            label: 'Capital Gains Tax (33%)'
          },
          income: {
            standardRateCutoff: 44000, // Single person standard rate band (2026)
            standardRate: 0.20, // 20% standard rate
            higherRate: 0.40, // 40% higher rate
            personalTaxCredit: 2000, // Personal tax credit (2026)
            employeeTaxCredit: 2000, // Earned Income tax credit (2026)
            prsiRate: 0.042, // 4.2% PRSI rate (current)
            // USC rates and thresholds (2026)
            usc: [
              { threshold: 12012, rate: 0.005 },    // 0.5% on first €12,012
              { threshold: 28700, rate: 0.02 },     // 2% on €12,012.01 to €28,700
              { threshold: 70044, rate: 0.03 },     // 3% on €28,700.01 to €70,044
              { threshold: Number.POSITIVE_INFINITY, rate: 0.08 }  // 8% on income over €70,044
            ],
            uscSurchargeThreshold: 100000,
            uscSurchargeRate: 0.03
          }
        };
    }
  }, [taxJurisdiction, usState]);
  
  // Calculate capital gains tax
  const cgtCalculation = React.useMemo(() => {
    // Filter for asset sales (capital transactions)
    const assetSales = transactions.filter(t => 
      t.type === 'income' && 
      t.category === 'assetSale'
    );
    
    // Calculate total capital gains
    const totalGains = assetSales.reduce((sum, sale) => sum + parseFloat(sale.amount || 0), 0);
    
    // Determine CGT rate and label based on jurisdiction
    let cgtRate = 0.33; // Default to Irish rate
    let cgtRateLabel = 'Capital Gains Tax (33%)';
    
    if (taxJurisdiction === 'United States') {
      // Simplified - assume long-term rate for US
      cgtRate = taxRates.cgt.longTermRate;
      cgtRateLabel = `Long-term Capital Gains (${cgtRate * 100}%)`;
    } else if (taxJurisdiction === 'United Kingdom') {
      // Simplified - assume higher rate for UK
      cgtRate = taxRates.cgt.higherRate;
      cgtRateLabel = `Capital Gains Tax (${cgtRate * 100}%)`;
    } else if (taxJurisdiction === 'Ireland') {
      cgtRate = taxRates.cgt.rate;
      cgtRateLabel = `Capital Gains Tax (${cgtRate * 100}%)`;
    }
    
    // Calculate tax due
    const taxDue = totalGains * cgtRate;
    
    return {
      totalGains,
      taxDue,
      cgtRate,
      cgtRateLabel
    };
  }, [transactions, updatedTransactions, taxJurisdiction, taxRates]);

  const capitalAllowanceSummary = React.useMemo(() => (
    calculateCapitalAllowanceSummary({
      assets: updatedTransactions,
      isCapitalExpense,
      taxYear
    })
  ), [isCapitalExpense, taxYear, updatedTransactions]);

  const corporationTaxAdjustedProfit = React.useMemo(() => {
    const accountingProfitAfterDepreciation = Number(adjustedNetProfit) || 0;
    return parseFloat((
      accountingProfitAfterDepreciation +
      Number(capitalAllowanceSummary.bookDepreciationAddBack || 0) -
      Number(capitalAllowanceSummary.capitalAllowances || 0)
    ).toFixed(2));
  }, [
    adjustedNetProfit,
    capitalAllowanceSummary.bookDepreciationAddBack,
    capitalAllowanceSummary.capitalAllowances
  ]);
  
  // Jurisdiction-specific income tax calculation
  const incomeTaxCalculation = React.useMemo(() => {
    const accountingProfitAfterDepreciation = parseFloat((Number(adjustedNetProfit) || 0).toFixed(2));
    const taxAdjustedNetProfit = corporationTaxAdjustedProfit;
    const capitalAllowanceTaxAdjustment = {
      accountingProfitAfterDepreciation,
      bookDepreciationAddBack: capitalAllowanceSummary.bookDepreciationAddBack,
      capitalAllowances: capitalAllowanceSummary.capitalAllowances,
      capitalAllowanceRate: capitalAllowanceSummary.annualAllowanceRate,
      capitalAllowanceYears: capitalAllowanceSummary.capitalAllowanceYears,
      capitalAllowanceRows: capitalAllowanceSummary.rows,
      taxAdjustedProfit: taxAdjustedNetProfit
    };

    if (isLimitedCompany) {
      return {
        ...calculateCorporationTax(corporationTaxAdjustedProfit, taxJurisdiction),
        ...capitalAllowanceTaxAdjustment
      };
    }

    // US Income Tax Calculation
    if (taxJurisdiction === 'United States') {
      // Get filing status and appropriate standard deduction
      const filingStatus = taxRates.income.filingStatus; // 'single' or 'married'
      const standardDeduction = taxRates.income.standardDeduction[filingStatus];
      
      // Calculate taxable income
      const taxableIncome = Math.max(0, taxAdjustedNetProfit - standardDeduction);
      
      // Get the appropriate tax brackets based on filing status
      const brackets = taxRates.income.brackets[filingStatus];
      
      // Calculate federal income tax using brackets
      let federalTax = 0;
      let remainingIncome = taxableIncome;
      let previousThreshold = 0;
      
      // Track tax by bracket for display
      const taxByBracket = [];
      
      for (const bracket of brackets) {
        const incomeInBracket = Math.min(
          remainingIncome,
          bracket.threshold - previousThreshold
        );
        
        if (incomeInBracket > 0) {
          const taxForBracket = incomeInBracket * bracket.rate;
          federalTax += taxForBracket;
          
          // Store bracket information for display
          taxByBracket.push({
            rate: bracket.rate,
            income: incomeInBracket,
            tax: taxForBracket,
            from: previousThreshold,
            to: bracket.threshold
          });
          
          remainingIncome -= incomeInBracket;
          previousThreshold = bracket.threshold;
          
          if (remainingIncome <= 0) break;
        }
      }
      
      // If there's still income left, apply the highest bracket rate
      if (remainingIncome > 0 && brackets.length > 0) {
        const highestRate = brackets[brackets.length - 1].rate;
        const taxForHighestBracket = remainingIncome * highestRate;
        federalTax += taxForHighestBracket;
        
        // Store highest bracket information
        taxByBracket.push({
          rate: highestRate,
          income: remainingIncome,
          tax: taxForHighestBracket,
          from: previousThreshold,
          to: 'and above'
        });
      }
      
      // Calculate Self-Employment Tax (Social Security and Medicare)
      // Social Security portion (12.4%) - capped at wage base limit
      const socialSecurityIncome = Math.min(taxAdjustedNetProfit, taxRates.income.selfEmploymentTax.socialSecurityWageCap);
      const socialSecurity = socialSecurityIncome * taxRates.income.selfEmploymentTax.socialSecurityRate;
      
      // Medicare portion (2.9%) - no cap
      let medicare = taxAdjustedNetProfit * taxRates.income.selfEmploymentTax.medicareRate;
      
      // Additional Medicare Tax (0.9%) for high earners
      if (taxAdjustedNetProfit > taxRates.income.additionalMedicareThreshold) {
        medicare += (taxAdjustedNetProfit - taxRates.income.additionalMedicareThreshold) *
                    taxRates.income.additionalMedicareRate;
      }
      
      // Self-employment tax deduction (employer portion)
      const selfEmploymentTaxDeduction = (socialSecurity + medicare) * 0.5;
      
      // Calculate state income tax (if applicable)
      const selectedState = taxRates.income.selectedState;
      let stateIncomeTax = 0;
      let stateDeduction = 0;
      let stateRate = 0;
      let stateType = '';
      
      // Check if state has income tax
      if (taxRates.income.stateTax.noTaxStates.includes(selectedState)) {
        // No state income tax
        stateType = 'No income tax state';
      } else if (selectedState in taxRates.income.stateTax.flatRateStates) {
        // Flat rate state
        stateRate = taxRates.income.stateTax.flatRateStates[selectedState];
        stateType = 'Flat rate';
        
        // Apply state standard deduction if available
        stateDeduction = taxRates.income.stateTax.standardDeductions[selectedState] || 
                         taxRates.income.stateTax.standardDeductions.default;
                         
        stateIncomeTax = Math.max(0, taxAdjustedNetProfit - stateDeduction) * stateRate;
      } else if (selectedState in taxRates.income.stateTax.progressiveRateStates) {
        // Progressive rate state (simplified using highest marginal rate)
        stateRate = taxRates.income.stateTax.progressiveRateStates[selectedState];
        stateType = 'Progressive rate';
        
        // Apply state standard deduction if available
        stateDeduction = taxRates.income.stateTax.standardDeductions[selectedState] || 
                         taxRates.income.stateTax.standardDeductions.default;
                         
        stateIncomeTax = Math.max(0, taxAdjustedNetProfit - stateDeduction) * stateRate;
      }
      
      // Total tax liability (federal + self-employment + state income tax)
      const totalTaxLiability = federalTax + socialSecurity + medicare + stateIncomeTax;
      
      // Take-home pay
      const takeHomePay = adjustedNetProfit - totalTaxLiability;
      
      // Effective tax rate
      const effectiveTaxRate = adjustedNetProfit > 0 ? totalTaxLiability / adjustedNetProfit : 0;
      
      return {
        ...capitalAllowanceTaxAdjustment,
        filingStatus,
        standardDeduction,
        taxableIncome,
        federalIncomeTax: federalTax,
        taxByBracket,
        selfEmploymentTax: {
          socialSecurity,
          medicare,
          total: socialSecurity + medicare,
          deduction: selfEmploymentTaxDeduction
        },
        stateTax: {
          state: selectedState,
          type: stateType,
          rate: stateRate,
          deduction: stateDeduction,
          tax: stateIncomeTax
        },
        totalTaxLiability,
        takeHomePay,
        effectiveTaxRate
      };
    }
    // UK Income Tax Calculation
    else if (taxJurisdiction === 'United Kingdom') {
      // Calculate personal allowance (tapers for high earners)
      let personalAllowance = taxRates.income.personalAllowance;
      
      // Personal allowance is reduced by £1 for every £2 earned over £100,000
      if (taxAdjustedNetProfit > taxRates.income.personalAllowanceTaperThreshold) {
        const reduction = Math.min(
          personalAllowance, 
          (taxAdjustedNetProfit - taxRates.income.personalAllowanceTaperThreshold) * taxRates.income.personalAllowanceTaperRate
        );
        personalAllowance -= reduction;
      }
      
      // Taxable income after personal allowance
      const taxableIncome = Math.max(0, taxAdjustedNetProfit - personalAllowance);
      
      // Basic rate (20%)
      const basicRateBand = taxRates.income.basicRateThreshold - personalAllowance;
      const basicRateAmount = Math.min(taxableIncome, basicRateBand);
      const basicRateTax = basicRateAmount * taxRates.income.basicRate;
      
      // Higher rate (40%)
      const higherRateBand = taxRates.income.higherRateThreshold - taxRates.income.basicRateThreshold;
      const higherRateAmount = Math.min(
        Math.max(0, taxableIncome - basicRateBand),
        higherRateBand
      );
      const higherRateTax = higherRateAmount * taxRates.income.higherRate;
      
      // Additional rate (45%)
      const additionalRateAmount = Math.max(0, taxableIncome - basicRateBand - higherRateBand);
      const additionalRateTax = additionalRateAmount * taxRates.income.additionalRate;
      
      // Total income tax
      const incomeTax = basicRateTax + higherRateTax + additionalRateTax;
      
      // National Insurance contributions (Class 4) for 2025/26
      let nationalInsurance = 0;
      
      // 6% on profits between lower and upper threshold
      if (taxAdjustedNetProfit > taxRates.income.ni.lowerProfitsThreshold) {
        const lowerBandProfit = Math.min(
          taxAdjustedNetProfit - taxRates.income.ni.lowerProfitsThreshold,
          taxRates.income.ni.upperProfitsThreshold - taxRates.income.ni.lowerProfitsThreshold
        );
        nationalInsurance += lowerBandProfit * taxRates.income.ni.lowerProfitsRate;
      }
      
      // 2% on profits above upper threshold
      if (taxAdjustedNetProfit > taxRates.income.ni.upperProfitsThreshold) {
        const upperBandProfit = taxAdjustedNetProfit - taxRates.income.ni.upperProfitsThreshold;
        nationalInsurance += upperBandProfit * taxRates.income.ni.upperProfitsRate;
      }
      
      // Total tax liability
      const totalTaxLiability = incomeTax + nationalInsurance;
      
      // Take-home pay
      const takeHomePay = adjustedNetProfit - totalTaxLiability;
      
      // Effective tax rate
      const effectiveTaxRate = adjustedNetProfit > 0 ? totalTaxLiability / adjustedNetProfit : 0;
      
      return {
        ...capitalAllowanceTaxAdjustment,
        personalAllowance,
        taxableIncome,
        incomeTax,
        taxBreakdown: {
          basicRate: {
            amount: basicRateAmount,
            tax: basicRateTax,
            rate: taxRates.income.basicRate
          },
          higherRate: {
            amount: higherRateAmount,
            tax: higherRateTax,
            rate: taxRates.income.higherRate
          },
          additionalRate: {
            amount: additionalRateAmount,
            tax: additionalRateTax,
            rate: taxRates.income.additionalRate
          }
        },
        nationalInsurance,
        niBreakdown: {
          lowerBand: {
            amount: Math.min(
              Math.max(0, taxAdjustedNetProfit - taxRates.income.ni.lowerProfitsThreshold),
              taxRates.income.ni.upperProfitsThreshold - taxRates.income.ni.lowerProfitsThreshold
            ),
            rate: taxRates.income.ni.lowerProfitsRate,
            tax: Math.min(
              Math.max(0, taxAdjustedNetProfit - taxRates.income.ni.lowerProfitsThreshold),
              taxRates.income.ni.upperProfitsThreshold - taxRates.income.ni.lowerProfitsThreshold
            ) * taxRates.income.ni.lowerProfitsRate
          },
          upperBand: {
            amount: Math.max(0, taxAdjustedNetProfit - taxRates.income.ni.upperProfitsThreshold),
            rate: taxRates.income.ni.upperProfitsRate,
            tax: Math.max(0, taxAdjustedNetProfit - taxRates.income.ni.upperProfitsThreshold) * taxRates.income.ni.upperProfitsRate
          }
        },
        totalTaxLiability,
        takeHomePay,
        effectiveTaxRate
      };
    }
    // Irish Income Tax Calculation (default)
    else if (taxJurisdiction === 'Ireland') {
      // 2026 tax rates and bands for single person in Ireland
      const standardRateCutoff = taxRates.income.standardRateCutoff;
      const lowerRate = taxRates.income.standardRate;
      const higherRate = taxRates.income.higherRate;
      
      console.log(`Irish tax calculation (2026): Standard rate cutoff: €${standardRateCutoff}, Lower rate: ${lowerRate * 100}%, Higher rate: ${higherRate * 100}%`);
      console.log(`Irish tax calculation: Tax adjusted net profit: €${taxAdjustedNetProfit}`);
      
      // Calculate gross tax
      const standardRateTax = Math.min(taxAdjustedNetProfit, standardRateCutoff) * lowerRate;
      const higherRateTax = Math.max(0, taxAdjustedNetProfit - standardRateCutoff) * higherRate;
      const grossTax = standardRateTax + higherRateTax;
      
      console.log(`Irish tax calculation: Standard rate tax: €${standardRateTax}, Higher rate tax: €${higherRateTax}, Gross tax: €${grossTax}`);
      
      // Tax credits (2026 values)
      const personalTaxCredit = taxRates.income.personalTaxCredit;
      const earnedIncomeTaxCredit = taxRates.income.employeeTaxCredit;
      
      // Medical expenses tax credit (20% of qualifying expenses)
      // Find medical expenses from transactions
      const medicalExpenses = transactions
        .filter(t => (t.type === 'expense' || t.type === 'expenses') && t.category === 'medical_expenses')
        .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
      
      const medicalExpensesTaxCredit = medicalExpenses * 0.2; // 20% relief
      
      // Total tax credits
      const totalTaxCredits = personalTaxCredit + earnedIncomeTaxCredit + medicalExpensesTaxCredit;
      
      console.log(`Irish tax calculation: Personal tax credit: €${personalTaxCredit}, Earned income tax credit: €${earnedIncomeTaxCredit}, Medical expenses tax credit: €${medicalExpensesTaxCredit}, Total tax credits: €${totalTaxCredits}`);
      
      // Tax payable after credits
      const taxPayable = Math.max(0, grossTax - totalTaxCredits);
      
      console.log(`Irish tax calculation: Tax payable after credits: €${taxPayable}`);
      
      // PRSI (Pay Related Social Insurance) - 4.2% on all income for self-employed (current 2025 rate)
      // Updated to 4.2% from October 2025
      const prsiRate = taxRates.income.prsiRate;
      const prsi = taxAdjustedNetProfit < 5000 ? 0 : taxAdjustedNetProfit * prsiRate;
      
      console.log(`Irish tax calculation: PRSI rate: ${prsiRate * 100}%, PRSI: ${prsi}`);
      
      // Calculate USC - No USC is payable on profit less than €13,000 per year
      let usc = 0;
      
      // Only calculate USC if profit is €13,000 or more
      if (taxAdjustedNetProfit >= 13000) {
        let remainingIncome = taxAdjustedNetProfit;
        let previousThreshold = 0;
        
        for (const band of taxRates.income.usc) {
          const cap = Number.isFinite(band.threshold) ? Math.max(0, band.threshold - previousThreshold) : remainingIncome;
          const incomeInBand = Math.min(Math.max(0, remainingIncome), cap);
          
          if (incomeInBand > 0) {
            const bandUsc = incomeInBand * band.rate;
            usc += bandUsc;
            console.log(`USC band: €${previousThreshold} to €${band.threshold}, Rate: ${band.rate * 100}%, Amount: €${incomeInBand}, USC: €${bandUsc}`);
            
            remainingIncome -= incomeInBand;
            previousThreshold = band.threshold;
            
            if (remainingIncome <= 0) break;
          }
        }

        // Self-employed USC surcharge on income over €100,000
        const surchargeThreshold = taxRates.income.uscSurchargeThreshold || 100000;
        if (taxAdjustedNetProfit > surchargeThreshold) {
          const surchargeRate = taxRates.income.uscSurchargeRate || 0.03;
          const surchargeIncome = taxAdjustedNetProfit - surchargeThreshold;
          const surcharge = surchargeIncome * surchargeRate;
          usc += surcharge;
          console.log(`USC surcharge: €${surchargeIncome} at ${surchargeRate * 100}% = €${surcharge}`);
        }
      } else {
        console.log(`Irish tax calculation: No USC payable as profit is less than €13,000`);
      }
      
      console.log(`Irish tax calculation: Total USC: €${usc}`);
      
      // Total tax liability
      const totalTaxLiability = taxPayable + prsi + usc;
      
      // Effective tax rate
      const effectiveTaxRate = adjustedNetProfit > 0 ? totalTaxLiability / adjustedNetProfit : 0;
      
      console.log(`Irish tax calculation: Total tax liability: €${totalTaxLiability}, Effective tax rate: ${effectiveTaxRate * 100}%`);
      
      return {
        ...capitalAllowanceTaxAdjustment,
        jurisdiction: 'Ireland',
        taxableIncome: taxAdjustedNetProfit,
        grossTax,
        taxCredits: {
          personal: personalTaxCredit,
          earnedIncome: earnedIncomeTaxCredit,
          medicalExpenses: medicalExpensesTaxCredit,
          total: totalTaxCredits
        },
        medicalExpenses,
        incomeTaxPayable: taxPayable,
        prsi,
        usc,
        totalTaxLiability,
        effectiveTaxRate,
        takeHomePay: adjustedNetProfit - totalTaxLiability,
        prsiNote: "Current PRSI rate is 4.2% (updated Oct 2025)"
      };
    }
    // France Income Tax Calculation
    else if (taxJurisdiction === 'France') {
      // Calculate standard deduction (10% of income, capped)
      const standardDeduction = Math.min(taxAdjustedNetProfit * 0.1, taxRates.income.maxStandardDeduction);
      const taxableIncome = Math.max(0, taxAdjustedNetProfit - standardDeduction);
      
      // Calculate progressive income tax
      let incomeTax = 0;
      let previousThreshold = 0;
      
      for (const bracket of taxRates.income.brackets) {
        const incomeInBracket = Math.min(
          Math.max(0, taxableIncome - previousThreshold),
          bracket.threshold - previousThreshold
        );
        
        if (incomeInBracket > 0) {
          incomeTax += incomeInBracket * bracket.rate;
          previousThreshold = bracket.threshold;
        }
        
        if (previousThreshold >= taxableIncome) break;
      }
      
      // If income exceeds the highest threshold, apply the highest rate to the remainder
      if (taxableIncome > previousThreshold && taxRates.income.brackets.length > 0) {
        const highestRate = taxRates.income.brackets[taxRates.income.brackets.length - 1].rate;
        incomeTax += (taxableIncome - previousThreshold) * highestRate;
      }
      
      // Calculate social charges (CSG, CRDS, etc.)
      const socialCharges = taxableIncome * taxRates.income.socialCharges;
      
      // Total tax liability
      const totalTaxLiability = incomeTax + socialCharges;
      
      // Effective tax rate
      const effectiveTaxRate = adjustedNetProfit > 0 ? totalTaxLiability / adjustedNetProfit : 0;
      
      return {
        ...capitalAllowanceTaxAdjustment,
        jurisdiction: 'France',
        grossIncome: adjustedNetProfit,
        standardDeduction,
        taxableIncome,
        incomeTax,
        socialCharges,
        totalTaxLiability,
        effectiveTaxRate,
        takeHomePay: adjustedNetProfit - totalTaxLiability
      };
    }
    // Germany Income Tax Calculation
    else if (taxJurisdiction === 'Germany') {
      // Calculate taxable income (no standard deduction in German system)
      const taxableIncome = Math.max(0, taxAdjustedNetProfit - taxRates.income.basicAllowance);
      
      // Calculate income tax using progressive formula for 2025
      let incomeTax = 0;
      
      if (taxableIncome <= 0) {
        incomeTax = 0;
      } else if (taxableIncome <= (taxRates.income.brackets[2].threshold - taxRates.income.brackets[1].threshold)) {
        // Progressive formula for first zone (14% to 42%)
        // Simplified approximation of the progressive formula
        const progressiveRate = 0.14 + (taxableIncome / (taxRates.income.brackets[2].threshold - taxRates.income.brackets[1].threshold)) * (0.42 - 0.14);
        incomeTax = taxableIncome * progressiveRate;
      } else if (taxableIncome <= (taxRates.income.brackets[3].threshold - taxRates.income.brackets[1].threshold)) {
        // Flat 42% for third zone
        incomeTax = 0.42 * taxableIncome;
      } else {
        // Flat 45% for highest zone
        incomeTax = 0.45 * taxableIncome;
      }
      
      // Calculate solidarity surcharge (if applicable)
      let solidaritySurcharge = 0;
      if (incomeTax > taxRates.income.solidaritySurchargeThreshold) {
        solidaritySurcharge = incomeTax * taxRates.income.solidaritySurcharge;
      }
      
      // Calculate church tax (if applicable - assuming the person is registered)
      const churchTax = incomeTax * taxRates.income.churchTax;
      
      // Calculate municipal trade tax (Gewerbesteuer) for sole traders
      let tradeTax = 0;
      const tradeTaxableIncome = Math.max(0, taxAdjustedNetProfit - taxRates.income.tradeTax.allowance);
      
      if (tradeTaxableIncome > 0) {
        // Get the municipality multiplier (default to Berlin if not specified)
        const municipality = "Berlin"; // This could be made configurable
        const multiplier = taxRates.income.tradeTax.municipalityMultipliers[municipality] || 
                          taxRates.income.tradeTax.municipalityMultipliers[taxRates.income.tradeTax.defaultMunicipality];
        
        // Calculate effective trade tax rate
        const effectiveTradeRate = taxRates.income.tradeTax.baseRate * (multiplier / 100);
        
        // Calculate trade tax
        tradeTax = tradeTaxableIncome * effectiveTradeRate;
        
        // Offset mechanism: Sole proprietors can offset trade tax against income tax
        // This is a simplified implementation of the offset
        const tradeTaxOffset = Math.min(tradeTax, incomeTax * 0.4); // Offset up to 4x the base rate
        incomeTax = Math.max(0, incomeTax - tradeTaxOffset);
      }
      
      // Total tax liability
      const totalTaxLiability = incomeTax + solidaritySurcharge + churchTax + tradeTax;
      
      // Effective tax rate
      const effectiveTaxRate = adjustedNetProfit > 0 ? totalTaxLiability / adjustedNetProfit : 0;
      
      // Calculate effective trade tax rate for display
      const municipality = "Berlin"; // This could be made configurable
      const multiplier = taxRates.income.tradeTax.municipalityMultipliers[municipality] || 
                        taxRates.income.tradeTax.municipalityMultipliers[taxRates.income.tradeTax.defaultMunicipality];
      const effectiveTradeRate = taxRates.income.tradeTax.baseRate * (multiplier / 100);
      
      return {
        ...capitalAllowanceTaxAdjustment,
        jurisdiction: 'Germany',
        grossIncome: adjustedNetProfit,
        basicAllowance: taxRates.income.basicAllowance,
        taxableIncome,
        incomeTax,
        solidaritySurcharge,
        churchTax,
        tradeTax,
        tradeTaxInfo: {
          municipality,
          multiplier,
          effectiveRate: effectiveTradeRate,
          allowance: taxRates.income.tradeTax.allowance,
          taxableIncome: tradeTaxableIncome
        },
        totalTaxLiability,
        effectiveTaxRate,
        takeHomePay: adjustedNetProfit - totalTaxLiability
      };
    }
    // Italy Income Tax Calculation
    else if (taxJurisdiction === 'Italy') {
      // Determine if using standard IRPEF or flat-rate regime
      // For this implementation, we'll assume the business is eligible for the flat-rate regime
      // and has been operating for less than 5 years (this could be made configurable)
      const usesFlatRate = true; // This could be made configurable
      const isFirstFiveYears = true; // This could be made configurable
      
      if (usesFlatRate && taxAdjustedNetProfit <= taxRates.income.flatRate.eligibleThreshold) {
        // Flat-rate regime calculation (Regime Forfettario)
        const flatRate = isFirstFiveYears ? 
                        taxRates.income.flatRate.firstFiveYears : 
                        taxRates.income.flatRate.afterFiveYears;
        
        // No regional or municipal taxes in flat-rate regime
        const flatRateTax = taxAdjustedNetProfit * flatRate;
        const totalTaxLiability = flatRateTax;
        const effectiveTaxRate = adjustedNetProfit > 0 ? totalTaxLiability / adjustedNetProfit : 0;
        
        return {
          ...capitalAllowanceTaxAdjustment,
          jurisdiction: 'Italy',
          regime: 'Flat-Rate (Regime Forfettario)',
          grossIncome: adjustedNetProfit,
          flatRate,
          flatRateTax,
          isFirstFiveYears,
          totalTaxLiability,
          effectiveTaxRate,
          takeHomePay: adjustedNetProfit - totalTaxLiability
        };
      } else {
        // Standard IRPEF calculation
        const taxableIncome = taxAdjustedNetProfit;
        
        // Calculate IRPEF (national income tax) using 2025 rates
        let irpef = 0;
        let previousThreshold = 0;
        
        for (const bracket of taxRates.income.brackets) {
          const incomeInBracket = Math.min(
            Math.max(0, taxableIncome - previousThreshold),
            bracket.threshold - previousThreshold
          );
          
          if (incomeInBracket > 0) {
            irpef += incomeInBracket * bracket.rate;
            previousThreshold = bracket.threshold;
          }
          
          if (previousThreshold >= taxableIncome) break;
        }
        
        // If income exceeds the highest threshold, apply the highest rate to the remainder
        if (taxableIncome > previousThreshold && taxRates.income.brackets.length > 0) {
          const highestRate = taxRates.income.brackets[taxRates.income.brackets.length - 1].rate;
          irpef += (taxableIncome - previousThreshold) * highestRate;
        }
        
        // Calculate regional and municipal taxes
        const regionalTax = taxableIncome * taxRates.income.regionalTax;
        const municipalTax = taxableIncome * taxRates.income.municipalTax;
        
        // Apply deductions
        const employmentDeduction = Math.min(taxRates.income.deductions.employment, irpef);
        
        // Calculate medical expenses deduction
        const medicalExpenses = transactions
          .filter(t => (t.type === 'expense' || t.type === 'expenses') && t.category === 'medical_expenses')
          .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
        
        const medicalDeduction = medicalExpenses * taxRates.income.deductions.medical;
        
        // Total deductions
        const totalDeductions = employmentDeduction + medicalDeduction;
        
        // Net IRPEF after deductions
        const netIrpef = Math.max(0, irpef - totalDeductions);
        
        // Total tax liability
        const totalTaxLiability = netIrpef + regionalTax + municipalTax;
        
        // Effective tax rate
        const effectiveTaxRate = adjustedNetProfit > 0 ? totalTaxLiability / adjustedNetProfit : 0;
        
        return {
          ...capitalAllowanceTaxAdjustment,
          jurisdiction: 'Italy',
          regime: 'Standard IRPEF',
          grossIncome: adjustedNetProfit,
          taxableIncome,
          irpef,
          regionalTax,
          municipalTax,
          deductions: {
            employment: employmentDeduction,
            medical: medicalDeduction,
            total: totalDeductions
          },
          netIrpef,
          totalTaxLiability,
          effectiveTaxRate,
          takeHomePay: adjustedNetProfit - totalTaxLiability
        };
      }
    }
    // Spain Income Tax Calculation
    else if (taxJurisdiction === 'Spain') {
      // Calculate taxable income
      const taxableIncome = Math.max(0, taxAdjustedNetProfit - taxRates.income.personalAllowance);
      
      // Calculate employment allowance (if applicable)
      let employmentAllowance = 0;
      if (taxableIncome <= taxRates.income.employmentAllowance.maxIncome) {
        employmentAllowance = taxRates.income.employmentAllowance.base;
      } else {
        // Allowance decreases as income increases
        employmentAllowance = Math.max(
          taxRates.income.employmentAllowance.minAllowance,
          taxRates.income.employmentAllowance.base - 
            (taxRates.income.employmentAllowance.base * 
             (taxableIncome - taxRates.income.employmentAllowance.maxIncome) / 
             taxRates.income.employmentAllowance.maxIncome)
        );
      }
      
      const finalTaxableIncome = Math.max(0, taxableIncome - employmentAllowance);
      
      // Calculate income tax (combined state and regional rates for 2025)
      let incomeTax = 0;
      let previousThreshold = 0;
      
      for (const bracket of taxRates.income.stateBrackets) {
        const incomeInBracket = Math.min(
          Math.max(0, finalTaxableIncome - previousThreshold),
          bracket.threshold - previousThreshold
        );
        
        if (incomeInBracket > 0) {
          incomeTax += incomeInBracket * bracket.rate;
          previousThreshold = bracket.threshold;
        }
        
        if (previousThreshold >= finalTaxableIncome) break;
      }
      
      // If income exceeds the highest threshold, apply the highest rate to the remainder
      if (finalTaxableIncome > previousThreshold && taxRates.income.stateBrackets.length > 0) {
        const highestRate = taxRates.income.stateBrackets[taxRates.income.stateBrackets.length - 1].rate;
        incomeTax += (finalTaxableIncome - previousThreshold) * highestRate;
      }
      
      // Calculate Social Security contributions (RETA)
      // Convert annual income to monthly for base calculation
      const monthlyIncome = taxAdjustedNetProfit / 12;
      
      // Determine contribution base (capped between min and max)
      const contributionBase = Math.min(
        Math.max(monthlyIncome, taxRates.income.socialSecurity.minMonthlyBase),
        taxRates.income.socialSecurity.maxMonthlyBase
      );
      
      // Calculate standard social security contribution
      const monthlySSContribution = contributionBase * taxRates.income.socialSecurity.rate;
      
      // Calculate solidarity contribution for high incomes
      let solidarityContribution = 0;
      if (monthlyIncome > taxRates.income.socialSecurity.maxMonthlyBase) {
        // Find the applicable solidarity rate
        let solidarityRate = 0;
        for (let i = taxRates.income.socialSecurity.solidarityContributions.length - 1; i >= 0; i--) {
          const threshold = taxRates.income.socialSecurity.solidarityContributions[i].threshold;
          if (monthlyIncome >= threshold) {
            solidarityRate = taxRates.income.socialSecurity.solidarityContributions[i].rate;
            break;
          }
        }
        
        // Calculate the solidarity contribution
        solidarityContribution = (monthlyIncome - taxRates.income.socialSecurity.maxMonthlyBase) * solidarityRate;
      }
      
      // Total monthly social security contribution
      const totalMonthlySSContribution = monthlySSContribution + solidarityContribution;
      
      // Annual social security contribution
      const socialSecurityContribution = totalMonthlySSContribution * 12;
      
      // Total tax liability (income tax + social security)
      const totalTaxLiability = incomeTax + socialSecurityContribution;
      
      // Effective tax rate
      const effectiveTaxRate = adjustedNetProfit > 0 ? totalTaxLiability / adjustedNetProfit : 0;
      
      // Calculate breakdown of income tax by brackets for display
      const taxByBracket = [];
      previousThreshold = 0;
      
      for (const bracket of taxRates.income.stateBrackets) {
        const incomeInBracket = Math.min(
          Math.max(0, finalTaxableIncome - previousThreshold),
          bracket.threshold - previousThreshold
        );
        
        if (incomeInBracket > 0) {
          taxByBracket.push({
            rate: bracket.rate,
            income: incomeInBracket,
            tax: incomeInBracket * bracket.rate,
            from: previousThreshold,
            to: bracket.threshold
          });
          
          previousThreshold = bracket.threshold;
        }
        
        if (previousThreshold >= finalTaxableIncome) break;
      }
      
      // If income exceeds the highest threshold, add the highest bracket
      if (finalTaxableIncome > previousThreshold && taxRates.income.stateBrackets.length > 0) {
        const highestBracket = taxRates.income.stateBrackets[taxRates.income.stateBrackets.length - 1];
        taxByBracket.push({
          rate: highestBracket.rate,
          income: finalTaxableIncome - previousThreshold,
          tax: (finalTaxableIncome - previousThreshold) * highestBracket.rate,
          from: previousThreshold,
          to: 'and above'
        });
      }
      
      return {
        ...capitalAllowanceTaxAdjustment,
        jurisdiction: 'Spain',
        grossIncome: adjustedNetProfit,
        personalAllowance: taxRates.income.personalAllowance,
        employmentAllowance,
        taxableIncome: finalTaxableIncome,
        incomeTax,
        taxByBracket,
        socialSecurity: {
          monthlyBase: contributionBase,
          monthlyContribution: monthlySSContribution,
          solidarityContribution: solidarityContribution * 12,
          totalAnnual: socialSecurityContribution,
          rate: taxRates.income.socialSecurity.rate
        },
        totalTaxLiability,
        effectiveTaxRate,
        takeHomePay: adjustedNetProfit - totalTaxLiability
      };
    }
    // Default tax calculation for other countries
    else {
      // Use a simplified calculation for other countries
      // Assume a flat 25% tax rate as a reasonable default
      const flatTaxRate = 0.25;
      const incomeTax = taxAdjustedNetProfit * flatTaxRate;
      const totalTaxLiability = incomeTax;
      const takeHomePay = adjustedNetProfit - totalTaxLiability;
      const effectiveTaxRate = adjustedNetProfit > 0 ? totalTaxLiability / adjustedNetProfit : 0;
      
      return {
        ...capitalAllowanceTaxAdjustment,
        jurisdiction: countryName,
        taxableIncome: taxAdjustedNetProfit,
        incomeTax,
        totalTaxLiability,
        effectiveTaxRate,
        takeHomePay,
        note: 'Simplified tax calculation using a flat rate of 25%'
      };
    }
  }, [
    adjustedNetProfit,
    capitalAllowanceSummary.annualAllowanceRate,
    capitalAllowanceSummary.bookDepreciationAddBack,
    capitalAllowanceSummary.capitalAllowanceYears,
    capitalAllowanceSummary.capitalAllowances,
    capitalAllowanceSummary.rows,
    corporationTaxAdjustedProfit,
    countryName,
    isLimitedCompany,
    taxJurisdiction,
    taxRates,
    transactions
  ]);

  const getTransactionVatAmount = React.useCallback((transaction) => {
    if (typeof buildTransactionVatEntries === 'function') {
      const vatEntries = buildTransactionVatEntries(transaction);
      if (vatEntries.length > 0) {
        if (vatEntries.some((entry) => entry?.vatBox)) {
          const boxTotals = vatEntries.reduce((acc, entry) => {
            const amount = Number(entry?.vatAmount) || 0;
            if (entry?.vatBox === 'sales') acc.sales += amount;
            if (entry?.vatBox === 'purchases') acc.purchases += amount;
            return acc;
          }, { sales: 0, purchases: 0 });
          return parseFloat(Math.max(boxTotals.sales, boxTotals.purchases).toFixed(2));
        }
        return parseFloat(vatEntries.reduce((sum, entry) => sum + (Number(entry?.vatAmount) || 0), 0).toFixed(2));
      }
    }

    return getTransactionVatAmountForReporting(transaction, {
      vatAccountingBasis: vatSettings.accountingBasis
    });
  }, [buildTransactionVatEntries, vatSettings.accountingBasis]);

  const getTransactionVatBoxAmounts = React.useCallback((transaction) => {
    if (typeof buildTransactionVatEntries === 'function') {
      const entries = buildTransactionVatEntries(transaction);
      if (entries.some((entry) => entry?.vatBox)) {
        return entries.reduce((acc, entry) => {
          const amount = Number(entry?.vatAmount) || 0;
          if (entry?.vatBox === 'sales') acc.vatCollected += amount;
          if (entry?.vatBox === 'purchases') acc.vatPaid += amount;
          return acc;
        }, { vatCollected: 0, vatPaid: 0 });
      }
    }

    const vatAmount = Number(getTransactionVatAmount(transaction) || 0);
    if (!Number.isFinite(vatAmount) || vatAmount <= 0) return { vatCollected: 0, vatPaid: 0 };
    const txType = String(transaction?.type || '').toLowerCase();
    if (txType === 'income') return { vatCollected: vatAmount, vatPaid: 0 };
    if (txType === 'expense' || txType === 'expenses') return { vatCollected: 0, vatPaid: vatAmount };
    return { vatCollected: 0, vatPaid: 0 };
  }, [buildTransactionVatEntries, getTransactionVatAmount]);

  const vatTransactionsSummary = React.useMemo(() => {
    const safeTransactions = Array.isArray(transactions) ? transactions : [];
    let vatCollected = 0;
    let vatPaid = 0;

    safeTransactions.forEach((transaction) => {
      const boxAmounts = getTransactionVatBoxAmounts(transaction);
      vatCollected += Number(boxAmounts.vatCollected) || 0;
      vatPaid += Number(boxAmounts.vatPaid) || 0;
    });

    vatCollected = parseFloat(vatCollected.toFixed(2));
    vatPaid = parseFloat(vatPaid.toFixed(2));
    const vatDue = parseFloat((vatCollected - vatPaid).toFixed(2));

    return { vatCollected, vatPaid, vatDue };
  }, [getTransactionVatBoxAmounts, transactions]);

  // Push a compact summary up to consumers (e.g., Business Analytics widget)
  useEffect(() => {
    if (typeof onTaxSummaryChange !== 'function') return;
    if (!incomeTaxCalculation) return;

    const totalTaxLiability =
      incomeTaxCalculation.totalTaxLiability ??
      incomeTaxCalculation.incomeTaxPayable ??
      incomeTaxCalculation.incomeTax ??
      0;

    const isCorporationTax = incomeTaxCalculation.calculationType === 'corporation_tax';
    const incomeTaxComponent =
      isCorporationTax
        ? 0
        : (
            incomeTaxCalculation.incomeTaxPayable ??
            incomeTaxCalculation.incomeTax ??
            incomeTaxCalculation.federalIncomeTax ??
            0
          );

    onTaxSummaryChange({
      taxType: isCorporationTax ? 'corporation_tax' : 'income_tax',
      taxLabel: isCorporationTax ? (incomeTaxCalculation.taxLabel || 'Corporation Tax') : 'Income Tax',
      taxYear,
      businessStructure: businessStructureLabel,
      accountingProfitAfterDepreciation: Number(incomeTaxCalculation.accountingProfitAfterDepreciation) || 0,
      bookDepreciationAddBack: Math.max(0, incomeTaxCalculation.bookDepreciationAddBack || 0),
      capitalAllowances: Math.max(0, incomeTaxCalculation.capitalAllowances || 0),
      taxAdjustedProfit: Number(incomeTaxCalculation.taxAdjustedProfit ?? incomeTaxCalculation.taxableProfit ?? incomeTaxCalculation.taxableIncome ?? 0) || 0,
      taxableProfit: Number(incomeTaxCalculation.taxableProfit ?? incomeTaxCalculation.taxAdjustedProfit ?? incomeTaxCalculation.taxableIncome ?? 0) || 0,
      capitalAllowanceRate: Math.max(0, incomeTaxCalculation.capitalAllowanceRate || 0),
      capitalAllowanceYears: incomeTaxCalculation.capitalAllowanceYears || 0,
      capitalAllowanceRows: Array.isArray(incomeTaxCalculation.capitalAllowanceRows) ? incomeTaxCalculation.capitalAllowanceRows : [],
      totalTaxLiability: Math.max(0, totalTaxLiability),
      effectiveTaxRate: Math.max(0, incomeTaxCalculation.effectiveTaxRate || 0),
      incomeTax: Math.max(0, incomeTaxComponent),
      corporationTax: isCorporationTax ? Math.max(0, incomeTaxCalculation.corporationTax || totalTaxLiability || 0) : 0,
      profitAfterTax: isCorporationTax ? (incomeTaxCalculation.profitAfterTax || 0) : 0,
      usc: Math.max(0, incomeTaxCalculation.usc || 0),
      prsi: Math.max(0, incomeTaxCalculation.prsi || 0),
      vatCollected: vatTransactionsSummary.vatCollected || 0,
      vatPaid: vatTransactionsSummary.vatPaid || 0,
      vatDue: vatTransactionsSummary.vatDue || 0
    });
  }, [businessStructureLabel, incomeTaxCalculation, onTaxSummaryChange, taxYear, vatTransactionsSummary]);
  
  const renderCorporationTaxSection = () => {
    const calculation = incomeTaxCalculation || {};

    return (
      <div className="bg-accent-gradient p-4 rounded-lg text-black">
        <h3 className="text-lg font-semibold text-black mb-3">
          {calculation.taxLabel || 'Corporation Tax'} ({calculation.taxYearLabel || '2026'})
        </h3>

        <div className="space-y-3">
          <div className="tax-row">
            <div className="tax-label text-black">Business Structure:</div>
            <div className="tax-value font-medium text-black">{businessStructureLabel}</div>
          </div>

          <div className="tax-row">
            <div className="tax-label text-black">Accounting Profit After Book Depreciation:</div>
            <div className="tax-value font-medium text-black">{formatCurrency(calculation.accountingProfitAfterDepreciation || 0)}</div>
          </div>

          <div className="tax-row">
            <div className="tax-label text-black">Add Back Book Depreciation:</div>
            <div className="tax-value font-medium text-black">+ {formatCurrency(calculation.bookDepreciationAddBack || 0)}</div>
          </div>

          <div className="tax-row">
            <div className="tax-label text-black">
              Less Capital Allowances ({((calculation.capitalAllowanceRate || 0.125) * 100).toFixed(1)}% over {calculation.capitalAllowanceYears || 8} years):
            </div>
            <div className="tax-value font-medium text-black">- {formatCurrency(calculation.capitalAllowances || 0)}</div>
          </div>

          {Array.isArray(calculation.capitalAllowanceRows) && calculation.capitalAllowanceRows.length > 0 && (
            <div className="rounded-md border border-black/10 bg-white/50 p-2 text-xs text-black">
              <div className="mb-1 font-semibold">Capital allowance assets for {capitalAllowanceSummary.taxYear}</div>
              <div className="space-y-1">
                {calculation.capitalAllowanceRows
                  .filter((row) => (row.bookDepreciation || 0) > 0 || (row.capitalAllowance || 0) > 0)
                  .slice(0, 6)
                  .map((row) => (
                    <div key={row.id} className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-2">
                      <span className="truncate">{row.description} ({row.depreciationYears} yr book)</span>
                      <span>Depn {formatCurrency(row.bookDepreciation || 0)}</span>
                      <span>CA {formatCurrency(row.capitalAllowance || 0)}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          <div className="tax-row tax-divider">
            <div className="tax-label text-black font-semibold">Taxable Profit:</div>
            <div className="tax-value font-semibold text-black">{formatCurrency(calculation.taxableProfit || 0)}</div>
          </div>

          <div className="tax-row">
            <div className="tax-label text-black">Rate / Method:</div>
            <div className="tax-value font-medium text-black">{calculation.rateLabel || '0%'}</div>
          </div>

          {Array.isArray(calculation.breakdown) && calculation.breakdown.map((entry, index) => (
            <div className="tax-row pl-6 text-sm" key={`${entry.label}-${index}`}>
              <div className="tax-label text-black">{entry.label}:</div>
              <div className="tax-value text-black">
                {entry.amount < 0 ? '-' : ''}{formatCurrency(Math.abs(entry.amount || 0))}
              </div>
            </div>
          ))}

          <div className="tax-row tax-divider">
            <div className="tax-label text-black font-semibold">Corporation Tax:</div>
            <div className="tax-value text-black font-semibold">{formatCurrency(calculation.corporationTax || 0)}</div>
          </div>

          <div className="tax-row">
            <div className="tax-label text-black font-semibold">Profit After Tax:</div>
            <div className="tax-value text-black font-semibold">{formatCurrency(calculation.profitAfterTax || 0)}</div>
          </div>

          <div className="tax-row">
            <div className="tax-label text-black text-sm">Effective Tax Rate:</div>
            <div className="tax-value text-black text-sm font-medium">{((calculation.effectiveTaxRate || 0) * 100).toFixed(1)}%</div>
          </div>

          {calculation.note && (
            <div className="mt-3 text-xs text-black italic">
              Note: {calculation.note}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSelfEmployedCapitalAllowanceRows = () => {
    const calculation = incomeTaxCalculation || {};
    const hasCapitalAllowanceAdjustment =
      (Number(calculation.bookDepreciationAddBack) || 0) !== 0 ||
      (Number(calculation.capitalAllowances) || 0) !== 0;

    if (isLimitedCompany || !hasCapitalAllowanceAdjustment) {
      return null;
    }

    return (
      <>
        <div className="tax-row">
          <div className="tax-label text-black">Add Back Book Depreciation:</div>
          <div className="tax-value font-medium text-black">+ {formatCurrency(calculation.bookDepreciationAddBack || 0)}</div>
        </div>

        <div className="tax-row">
          <div className="tax-label text-black">
            Less Capital Allowances ({((calculation.capitalAllowanceRate || 0.125) * 100).toFixed(1)}% over {calculation.capitalAllowanceYears || 8} years):
          </div>
          <div className="tax-value font-medium text-black">- {formatCurrency(calculation.capitalAllowances || 0)}</div>
        </div>

        <div className="tax-row tax-divider">
          <div className="tax-label text-black font-semibold">Tax Adjusted Profit:</div>
          <div className="tax-value font-semibold text-black">{formatCurrency(calculation.taxAdjustedProfit || 0)}</div>
        </div>
      </>
    );
  };

  // Render different tax sections based on jurisdiction
  const renderIncomeTaxSection = () => {
    if (isLimitedCompany) {
      return renderCorporationTaxSection();
    }

    if (taxJurisdiction === "United States") {
      // List of US states for dropdown
      const stateOptions = Object.keys(taxRates.income.salesTax).sort();
      
      return (
        <div className="tax-summary-card tax-section income-tax bg-accent-gradient text-black">
          <h3 className="text-lg font-semibold text-black mb-3">US Income Tax (2025)</h3>
          
          {/* Tax Settings with improved structure */}
          <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
            <h4 className="text-md font-semibold text-black mb-4">Tax Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-black mb-2">Filing Status:</label>
                <div className="tax-value font-medium text-black">Single (estimates only)</div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-black mb-2">State:</label>
                <select 
                  value={usState}
                  onChange={(e) => setUsState(e.target.value)}
                  className="tax-dropdown w-full"
                >
                  {stateOptions.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          {/* Tax calculations with improved spacing */}
          <div className="space-y-2">
            <div className="tax-row">
              <div className="tax-label text-black">Filing Status:</div>
              <div className="tax-value font-medium text-black">Single</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black">State:</div>
              <div className="tax-value font-medium text-black">{usState}</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black">Adjusted Net Profit:</div>
              <div className="tax-value font-medium text-black">{formatCurrency(adjustedNetProfit)}</div>
            </div>

            {renderSelfEmployedCapitalAllowanceRows()}
            
            <div className="tax-row">
              <div className="tax-label text-black">Standard Deduction:</div>
              <div className="tax-value font-medium text-black">- {formatCurrency(incomeTaxCalculation.standardDeduction)}</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black">Taxable Income:</div>
              <div className="tax-value font-medium text-black">{formatCurrency(incomeTaxCalculation.taxableIncome)}</div>
            </div>
            
            <div className="tax-row tax-divider">
              <div className="tax-label text-black font-semibold">Federal Income Tax:</div>
              <div className="tax-value font-semibold text-black">{formatCurrency(incomeTaxCalculation.federalIncomeTax)}</div>
            </div>
            
            {/* Nested tax breakdown */}
            {incomeTaxCalculation.taxByBracket && incomeTaxCalculation.taxByBracket.map((bracket, index) => (
              <div className="tax-row nested" key={index}>
                <div className="tax-label text-black text-sm">
                  {(bracket.rate * 100)}% on {bracket.from === 0 ? 'first' : `$${bracket.from.toLocaleString()} to`} 
                  {bracket.to === 'and above' ? ' and above' : ` $${bracket.to.toLocaleString()}`}:
                </div>
                <div className="tax-value text-sm text-black">{formatCurrency(bracket.tax)}</div>
              </div>
            ))}
            
            <div className="tax-row tax-divider">
              <div className="tax-label text-black font-semibold">Self-Employment Tax:</div>
              <div className="tax-value font-semibold text-black">{formatCurrency(incomeTaxCalculation.selfEmploymentTax.total)}</div>
            </div>
            
            <div className="tax-row nested">
              <div className="tax-label text-black text-sm">Social Security (12.4%):</div>
              <div className="tax-value text-sm text-black">{formatCurrency(incomeTaxCalculation.selfEmploymentTax.socialSecurity)}</div>
            </div>
            
            <div className="tax-row nested">
              <div className="tax-label text-black text-sm">Medicare (2.9%+):</div>
              <div className="tax-value text-sm text-black">{formatCurrency(incomeTaxCalculation.selfEmploymentTax.medicare)}</div>
            </div>
            
            <div className="tax-row nested">
              <div className="tax-label text-black text-sm">Employer Portion Deduction:</div>
              <div className="tax-value text-sm text-black">- {formatCurrency(incomeTaxCalculation.selfEmploymentTax.deduction)}</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black font-medium">State Income Tax ({incomeTaxCalculation.stateTax.state}):</div>
              <div className="tax-value font-medium text-black">{formatCurrency(incomeTaxCalculation.stateTax.tax)}</div>
            </div>
            
            {incomeTaxCalculation.stateTax.type && (
              <div className="tax-row nested">
                <div className="tax-label text-black text-sm">
                  {incomeTaxCalculation.stateTax.type} 
                  {incomeTaxCalculation.stateTax.rate > 0 && 
                    ` (${(incomeTaxCalculation.stateTax.rate * 100).toFixed(2)}%)`}
                </div>
                <div className="tax-value text-sm text-black"></div>
              </div>
            )}
            
            {incomeTaxCalculation.stateTax.deduction > 0 && (
              <div className="tax-row nested">
                <div className="tax-label text-black text-sm">State Deduction:</div>
                <div className="tax-value text-sm text-black">{formatCurrency(incomeTaxCalculation.stateTax.deduction)}</div>
              </div>
            )}
            
            {/* Final totals with emphasis */}
            <div className="tax-row tax-divider">
              <div className="tax-label text-black font-bold text-lg">Total Tax:</div>
              <div className="tax-value text-black font-bold text-lg">{formatCurrency(incomeTaxCalculation.totalTaxLiability)}</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black font-bold text-lg">Take Home Pay:</div>
              <div className="tax-value text-black font-bold text-lg">{formatCurrency(incomeTaxCalculation.takeHomePay)}</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black text-sm">Effective Tax Rate:</div>
              <div className="tax-value text-black text-sm font-medium">{(incomeTaxCalculation.effectiveTaxRate * 100).toFixed(1)}%</div>
            </div>
            
            <div className="mt-6 p-4 bg-blue-100 rounded-lg">
              <p className="text-sm text-black leading-relaxed">
                <strong>Note:</strong> This calculation includes federal income tax, self-employment tax (Social Security and Medicare), 
                and state income tax where applicable. Additional deductions like QBI may apply.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (taxJurisdiction === "United Kingdom") {
      return (
        <div className="bg-accent-gradient p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-black mb-3">
            UK Income Tax ({taxRates.income.taxYearLabel || '2026/27'})
          </h3>
          
          <div>
            <div className="tax-row">
              <div className="tax-label text-black">Adjusted Net Profit:</div>
              <div className="tax-value font-medium">{formatCurrency(adjustedNetProfit)}</div>
            </div>

            {renderSelfEmployedCapitalAllowanceRows()}
            
            <div className="tax-row">
              <div className="tax-label text-black">Personal Allowance:</div>
              <div className="tax-value font-medium">- {formatCurrency(incomeTaxCalculation.personalAllowance)}</div>
            </div>
            
            {(incomeTaxCalculation.taxAdjustedProfit || 0) > taxRates.income.personalAllowanceTaperThreshold && (
              <div className="tax-row pl-6 text-sm">
                <div className="tax-label text-black">
                  Reduced due to income over £{taxRates.income.personalAllowanceTaperThreshold.toLocaleString()}
                </div>
                <div className="tax-value"></div>
              </div>
            )}
            
            <div className="tax-row">
              <div className="tax-label text-black">Taxable Income:</div>
              <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.taxableIncome)}</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black font-medium">Income Tax:</div>
              <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.incomeTax)}</div>
            </div>
            
            {incomeTaxCalculation.taxBreakdown.basicRate.amount > 0 && (
              <div className="tax-row pl-6 text-sm">
                <div className="tax-label text-black">20% on £0 to £{(taxRates.income.basicRateThreshold - incomeTaxCalculation.personalAllowance).toLocaleString()}:</div>
                <div className="tax-value">{formatCurrency(incomeTaxCalculation.taxBreakdown.basicRate.tax)}</div>
              </div>
            )}
            
            {incomeTaxCalculation.taxBreakdown.higherRate.amount > 0 && (
              <div className="tax-row pl-6 text-sm">
                <div className="tax-label text-black">40% on £{(taxRates.income.basicRateThreshold - incomeTaxCalculation.personalAllowance).toLocaleString()} to £{(taxRates.income.higherRateThreshold - incomeTaxCalculation.personalAllowance).toLocaleString()}:</div>
                <div className="tax-value">{formatCurrency(incomeTaxCalculation.taxBreakdown.higherRate.tax)}</div>
              </div>
            )}
            
            {incomeTaxCalculation.taxBreakdown.additionalRate.amount > 0 && (
              <div className="tax-row pl-6 text-sm">
                <div className="tax-label text-black">45% on income over £{(taxRates.income.higherRateThreshold - incomeTaxCalculation.personalAllowance).toLocaleString()}:</div>
                <div className="tax-value">{formatCurrency(incomeTaxCalculation.taxBreakdown.additionalRate.tax)}</div>
              </div>
            )}
            
            <div className="tax-row mt-3">
              <div className="tax-label text-black font-medium">National Insurance (Class 4):</div>
              <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.nationalInsurance)}</div>
            </div>
            
            {incomeTaxCalculation.niBreakdown.lowerBand.amount > 0 && (
              <div className="tax-row pl-6 text-sm">
                <div className="tax-label text-black">
                  {(taxRates.income.ni.lowerProfitsRate * 100).toFixed(0)}% on £{taxRates.income.ni.lowerProfitsThreshold.toLocaleString()} to £{taxRates.income.ni.upperProfitsThreshold.toLocaleString()}:
                </div>
                <div className="tax-value">{formatCurrency(incomeTaxCalculation.niBreakdown.lowerBand.tax)}</div>
              </div>
            )}
            
            {incomeTaxCalculation.niBreakdown.upperBand.amount > 0 && (
              <div className="tax-row pl-6 text-sm">
                <div className="tax-label text-black">
                  {(taxRates.income.ni.upperProfitsRate * 100).toFixed(0)}% on income over £{taxRates.income.ni.upperProfitsThreshold.toLocaleString()}:
                </div>
                <div className="tax-value">{formatCurrency(incomeTaxCalculation.niBreakdown.upperBand.tax)}</div>
              </div>
            )}
            
            <div className="tax-row tax-divider">
              <div className="tax-label text-black font-medium">Total Tax:</div>
              <div className="tax-value text-black font-medium">{formatCurrency(incomeTaxCalculation.totalTaxLiability)}</div>
            </div>
            
            <div className="tax-row" style={{ marginTop: "8px" }}>
              <div className="tax-label text-black font-medium">Take Home Pay:</div>
              <div className="tax-value text-black font-medium">{formatCurrency(incomeTaxCalculation.takeHomePay)}</div>
            </div>
            
            <div className="tax-row" style={{ marginTop: "4px" }}>
              <div className="tax-label text-black text-xs">Effective Tax Rate:</div>
              <div className="tax-value text-black text-xs">{(incomeTaxCalculation.effectiveTaxRate * 100).toFixed(1)}%</div>
            </div>
            
            <div className="mt-3 text-xs text-black italic">
              Note: Personal Allowance reduces by £1 for every £2 of income above £{taxRates.income.personalAllowanceTaperThreshold.toLocaleString()}.
            </div>
          </div>
        </div>
      );
    }

    if (taxJurisdiction === "Ireland") {
      const {
        incomeTaxPayable,
        prsi,
        usc,
        totalTaxLiability,
        takeHomePay,
        effectiveTaxRate
      } = incomeTaxCalculation;

      return (
        <div className="bg-accent-gradient p-4 rounded-lg text-black">
          <h3 className="text-lg font-semibold text-black mb-3">Irish Income Tax Summary</h3>

          <div className="space-y-3">
            <div className="tax-row">
              <div className="tax-label text-black">Adjusted Net Profit:</div>
              <div className="tax-value font-medium text-black">{formatCurrency(adjustedNetProfit)}</div>
            </div>

            {renderSelfEmployedCapitalAllowanceRows()}

            <div className="tax-row">
              <div className="tax-label text-black">Income Tax (after credits):</div>
              <div className="tax-value font-medium text-black">{formatCurrency(incomeTaxPayable)}</div>
            </div>

            <div className="tax-row">
              <div className="tax-label text-black">PRSI:</div>
              <div className="tax-value font-medium text-black">{formatCurrency(prsi)}</div>
            </div>

            <div className="tax-row">
              <div className="tax-label text-black">USC:</div>
              <div className="tax-value font-medium text-black">{formatCurrency(usc)}</div>
            </div>

            <div className="tax-row tax-divider">
              <div className="tax-label text-black font-semibold">Total Tax:</div>
              <div className="tax-value text-black font-semibold">{formatCurrency(totalTaxLiability)}</div>
            </div>

            <div className="tax-row">
              <div className="tax-label text-black font-semibold">Take Home Pay:</div>
              <div className="tax-value text-black font-semibold">{formatCurrency(takeHomePay)}</div>
            </div>

            <div className="tax-row">
              <div className="tax-label text-black text-sm">Effective Tax Rate:</div>
              <div className="tax-value text-black text-sm font-medium">{(effectiveTaxRate * 100).toFixed(1)}%</div>
            </div>
          </div>
        </div>
      );
    }
    if (taxJurisdiction === "France") {
      const renderBracket = (label, value) => (
        <div className="tax-row pl-6 text-sm">
          <div className="tax-label text-black">{label}</div>
          <div className="tax-value">{value}</div>
        </div>
      );

      return (
        <div className="bg-accent-gradient p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-black mb-3">French Income Tax (2025)</h3>
          
          <div>
            <div className="tax-row">
              <div className="tax-label text-black">Gross Income:</div>
              <div className="tax-value font-medium">{formatCurrency(adjustedNetProfit)}</div>
            </div>

            {renderSelfEmployedCapitalAllowanceRows()}
            
            <div className="tax-row">
              <div className="tax-label text-black">Standard Deduction (10%):</div>
              <div className="tax-value font-medium">- {formatCurrency(incomeTaxCalculation.standardDeduction)}</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black">Taxable Income:</div>
              <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.taxableIncome)}</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black">Income Tax:</div>
              <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.incomeTax)}</div>
            </div>
            
            {renderBracket("0% on first \u20ac11,497:", formatCurrency(0))}
            
            {incomeTaxCalculation.taxableIncome > 11497 && renderBracket(
              "11% on \u20ac11,498 to \u20ac29,315:",
              formatCurrency(Math.min(incomeTaxCalculation.taxableIncome - 11497, 29315 - 11497) * 0.11)
            )}
            
            {incomeTaxCalculation.taxableIncome > 29315 && renderBracket(
              "30% on \u20ac29,316 to \u20ac83,823:",
              formatCurrency(Math.min(incomeTaxCalculation.taxableIncome - 29315, 83823 - 29315) * 0.30)
            )}
            
            {incomeTaxCalculation.taxableIncome > 83823 && renderBracket(
              "41% on \u20ac83,824 to \u20ac180,294:",
              formatCurrency(Math.min(incomeTaxCalculation.taxableIncome - 83823, 180294 - 83823) * 0.41)
            )}
            
            {incomeTaxCalculation.taxableIncome > 180294 && renderBracket(
              "45% on income over \u20ac180,294:",
              formatCurrency((incomeTaxCalculation.taxableIncome - 180294) * 0.45)
            )}
            
            <div className="tax-row">
              <div className="tax-label text-black">Social Charges (17.2%):</div>
              <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.socialCharges)}</div>
            </div>
            
            <div className="tax-row tax-divider">
              <div className="tax-label text-black font-medium">Total Tax:</div>
              <div className="tax-value text-black font-medium">{formatCurrency(incomeTaxCalculation.totalTaxLiability)}</div>
            </div>
            
            <div className="tax-row" style={{ marginTop: "8px" }}>
              <div className="tax-label text-black font-medium">Take Home Pay:</div>
              <div className="tax-value text-black font-medium">{formatCurrency(incomeTaxCalculation.takeHomePay)}</div>
            </div>
            
            <div className="tax-row" style={{ marginTop: "4px" }}>
              <div className="tax-label text-black text-xs">Effective Tax Rate:</div>
              <div className="tax-value text-black text-xs">{(incomeTaxCalculation.effectiveTaxRate * 100).toFixed(1)}%</div>
            </div>
          </div>
        </div>
      );
    }
    if (taxJurisdiction === "Germany") {
      return (
        <div className="bg-accent-gradient p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-black mb-3">German Income Tax (2025)</h3>
          
          <div>
            <div className="tax-row">
              <div className="tax-label text-black">Gross Income:</div>
              <div className="tax-value font-medium">{formatCurrency(adjustedNetProfit)}</div>
            </div>

            {renderSelfEmployedCapitalAllowanceRows()}
            
            <div className="tax-row">
              <div className="tax-label text-black">Basic Allowance:</div>
              <div className="tax-value font-medium">- {formatCurrency(incomeTaxCalculation.basicAllowance)}</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black">Taxable Income:</div>
              <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.taxableIncome)}</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black">Income Tax:</div>
              <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.incomeTax)}</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black">Solidarity Surcharge:</div>
              <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.solidaritySurcharge)}</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black">Church Tax:</div>
              <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.churchTax)}</div>
            </div>
            
            {/* Trade Tax Section */}
            <div className="tax-row mt-3">
              <div className="tax-label text-black font-medium">Trade Tax (Gewerbesteuer):</div>
              <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.tradeTax)}</div>
            </div>
            
            <div className="tax-row pl-6 text-sm">
              <div className="tax-label text-black">Municipality:</div>
              <div className="tax-value">{incomeTaxCalculation.tradeTaxInfo.municipality}</div>
            </div>
            
            <div className="tax-row pl-6 text-sm">
              <div className="tax-label text-black">Multiplier (Hebesatz):</div>
              <div className="tax-value">{incomeTaxCalculation.tradeTaxInfo.multiplier}%</div>
            </div>
            
            <div className="tax-row pl-6 text-sm">
              <div className="tax-label text-black">Effective Rate:</div>
              <div className="tax-value">{(incomeTaxCalculation.tradeTaxInfo.effectiveRate * 100).toFixed(2)}%</div>
            </div>
            
            <div className="tax-row pl-6 text-sm">
              <div className="tax-label text-black">Tax-Free Allowance:</div>
              <div className="tax-value">€{incomeTaxCalculation.tradeTaxInfo.allowance.toLocaleString()}</div>
            </div>
            
            <div className="tax-row pl-6 text-sm">
              <div className="tax-label text-black">Taxable Income:</div>
              <div className="tax-value">{formatCurrency(incomeTaxCalculation.tradeTaxInfo.taxableIncome)}</div>
            </div>
            
            <div className="tax-row tax-divider">
              <div className="tax-label text-black font-medium">Total Tax:</div>
              <div className="tax-value text-black font-medium">{formatCurrency(incomeTaxCalculation.totalTaxLiability)}</div>
            </div>
            
            <div className="tax-row" style={{ marginTop: "8px" }}>
              <div className="tax-label text-black font-medium">Take Home Pay:</div>
              <div className="tax-value text-black font-medium">{formatCurrency(incomeTaxCalculation.takeHomePay)}</div>
            </div>
            
            <div className="tax-row" style={{ marginTop: "4px" }}>
              <div className="tax-label text-black text-xs">Effective Tax Rate:</div>
              <div className="tax-value text-black text-xs">{(incomeTaxCalculation.effectiveTaxRate * 100).toFixed(1)}%</div>
            </div>
            
            <div className="mt-3 text-xs text-black italic">
              Note: Sole proprietors can offset trade tax against their income tax liability, which is reflected in the calculation.
            </div>
          </div>
        </div>
      );
    }

    if (taxJurisdiction === "Italy") {
      // Check which regime is being used
      if (incomeTaxCalculation.regime === 'Flat-Rate (Regime Forfettario)') {
        return (
          <div className="bg-accent-gradient p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-black mb-3">Italian Income Tax (2025) - Flat-Rate Regime</h3>
            
            <div>
              <div className="tax-row">
                <div className="tax-label text-black">Gross Income:</div>
                <div className="tax-value font-medium">{formatCurrency(adjustedNetProfit)}</div>
              </div>

              {renderSelfEmployedCapitalAllowanceRows()}
              
              <div className="tax-row">
                <div className="tax-label text-black">Flat Tax Rate:</div>
                <div className="tax-value font-medium">{(incomeTaxCalculation.flatRate * 100).toFixed(1)}%</div>
              </div>
              
              <div className="tax-row pl-6 text-sm">
                <div className="tax-label text-black">
                  {incomeTaxCalculation.isFirstFiveYears ? 
                    "First 5 years rate (5%)" : 
                    "Standard flat rate (15%)"}
                </div>
                <div className="tax-value"></div>
              </div>
              
              <div className="tax-row">
                <div className="tax-label text-black">Flat-Rate Tax:</div>
                <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.flatRateTax)}</div>
              </div>
              
              <div className="tax-row tax-divider">
                <div className="tax-label text-black font-medium">Total Tax:</div>
                <div className="tax-value text-black font-medium">{formatCurrency(incomeTaxCalculation.totalTaxLiability)}</div>
              </div>
              
              <div className="tax-row" style={{ marginTop: "8px" }}>
                <div className="tax-label text-black font-medium">Take Home Pay:</div>
                <div className="tax-value text-black font-medium">{formatCurrency(incomeTaxCalculation.takeHomePay)}</div>
              </div>
              
              <div className="tax-row" style={{ marginTop: "4px" }}>
                <div className="tax-label text-black text-xs">Effective Tax Rate:</div>
                <div className="tax-value text-black text-xs">{(incomeTaxCalculation.effectiveTaxRate * 100).toFixed(1)}%</div>
              </div>
              
              <div className="mt-3 text-xs text-black italic">
                Note: The Flat-Rate Regime (Regime Forfettario) is available to sole traders with annual income up to €85,000.
                It offers simplified accounting and a flat tax rate with no additional regional or municipal taxes.
              </div>
            </div>
          </div>
        );
      } else {
        return (
          <div className="bg-accent-gradient p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-black mb-3">Italian Income Tax (2025) - Standard Regime</h3>
            
            <div>
              <div className="tax-row">
                <div className="tax-label text-black">Gross Income:</div>
                <div className="tax-value font-medium">{formatCurrency(adjustedNetProfit)}</div>
              </div>

              {renderSelfEmployedCapitalAllowanceRows()}
              
              <div className="tax-row">
                <div className="tax-label text-black">IRPEF (National Tax):</div>
                <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.irpef)}</div>
              </div>
              
              <div className="tax-row pl-6 text-sm">
                <div className="tax-label text-black">23% on first €28,000:</div>
                <div className="tax-value">{formatCurrency(Math.min(incomeTaxCalculation.taxAdjustedProfit || 0, 28000) * 0.23)}</div>
              </div>
              
              {(incomeTaxCalculation.taxAdjustedProfit || 0) > 28000 && (
                <div className="tax-row pl-6 text-sm">
                  <div className="tax-label text-black">35% on €28,001 to €50,000:</div>
                  <div className="tax-value">{formatCurrency(Math.min((incomeTaxCalculation.taxAdjustedProfit || 0) - 28000, 50000 - 28000) * 0.35)}</div>
                </div>
              )}
              
              {(incomeTaxCalculation.taxAdjustedProfit || 0) > 50000 && (
                <div className="tax-row pl-6 text-sm">
                  <div className="tax-label text-black">43% on income over €50,000:</div>
                  <div className="tax-value">{formatCurrency(((incomeTaxCalculation.taxAdjustedProfit || 0) - 50000) * 0.43)}</div>
                </div>
              )}
              
              <div className="tax-row">
                <div className="tax-label text-black">Deductions:</div>
                <div className="tax-value font-medium">- {formatCurrency(incomeTaxCalculation.deductions.total)}</div>
              </div>
              
              <div className="tax-row pl-6 text-sm">
                <div className="tax-label text-black">Employment Deduction:</div>
                <div className="tax-value">- {formatCurrency(incomeTaxCalculation.deductions.employment)}</div>
              </div>
              
              <div className="tax-row pl-6 text-sm">
                <div className="tax-label text-black">Medical Expenses:</div>
                <div className="tax-value">- {formatCurrency(incomeTaxCalculation.deductions.medical)}</div>
              </div>
              
              <div className="tax-row">
                <div className="tax-label text-black">Net IRPEF:</div>
                <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.netIrpef)}</div>
              </div>
              
              <div className="tax-row">
                <div className="tax-label text-black">Regional Tax:</div>
                <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.regionalTax)}</div>
              </div>
              
              <div className="tax-row">
                <div className="tax-label text-black">Municipal Tax:</div>
                <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.municipalTax)}</div>
              </div>
              
              <div className="tax-row tax-divider">
                <div className="tax-label text-black font-medium">Total Tax:</div>
                <div className="tax-value text-black font-medium">{formatCurrency(incomeTaxCalculation.totalTaxLiability)}</div>
              </div>
              
              <div className="tax-row" style={{ marginTop: "8px" }}>
                <div className="tax-label text-black font-medium">Take Home Pay:</div>
                <div className="tax-value text-black font-medium">{formatCurrency(incomeTaxCalculation.takeHomePay)}</div>
              </div>
              
              <div className="tax-row" style={{ marginTop: "4px" }}>
                <div className="tax-label text-black text-xs">Effective Tax Rate:</div>
                <div className="tax-value text-black text-xs">{(incomeTaxCalculation.effectiveTaxRate * 100).toFixed(1)}%</div>
              </div>
              
              <div className="mt-3 text-xs text-black italic">
                Note: You may be eligible for the Flat-Rate Regime (Regime Forfettario) which offers a simplified 
                tax calculation with a flat rate of 5% (first 5 years) or 15% (after 5 years).
              </div>
            </div>
          </div>
        );
      }
    }

    if (taxJurisdiction === "Spain") {
      return (
        <div className="bg-accent-gradient p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-black mb-3">Spanish Income Tax (2025)</h3>
          
          <div>
            <div className="tax-row">
              <div className="tax-label text-black">Gross Income:</div>
              <div className="tax-value font-medium">{formatCurrency(adjustedNetProfit)}</div>
            </div>

            {renderSelfEmployedCapitalAllowanceRows()}
            
            <div className="tax-row">
              <div className="tax-label text-black">Personal Allowance:</div>
              <div className="tax-value font-medium">- {formatCurrency(incomeTaxCalculation.personalAllowance)}</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black">Employment Allowance:</div>
              <div className="tax-value font-medium">- {formatCurrency(incomeTaxCalculation.employmentAllowance)}</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black">Taxable Income:</div>
              <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.taxableIncome)}</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black font-medium">Income Tax:</div>
              <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.incomeTax)}</div>
            </div>
            
            {/* Show tax breakdown by bracket */}
            {incomeTaxCalculation.taxByBracket && incomeTaxCalculation.taxByBracket.map((bracket, index) => (
              <div className="tax-row pl-6 text-sm" key={index}>
                <div className="tax-label text-black">
                  {bracket.rate * 100}% on {bracket.from === 0 ? 'first' : `€${bracket.from.toLocaleString()} to`} 
                  {bracket.to === 'and above' ? ' and above' : ` €${bracket.to.toLocaleString()}`}:
                </div>
                <div className="tax-value">{formatCurrency(bracket.tax)}</div>
              </div>
            ))}
            
            <div className="tax-row mt-3">
              <div className="tax-label text-black font-medium">Social Security (RETA):</div>
              <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.socialSecurity.totalAnnual)}</div>
            </div>
            
            <div className="tax-row pl-6 text-sm">
              <div className="tax-label text-black">Monthly Base:</div>
              <div className="tax-value">{formatCurrency(incomeTaxCalculation.socialSecurity.monthlyBase)}</div>
            </div>
            
            <div className="tax-row pl-6 text-sm">
              <div className="tax-label text-black">Contribution Rate:</div>
              <div className="tax-value">{(incomeTaxCalculation.socialSecurity.rate * 100).toFixed(1)}%</div>
            </div>
            
            <div className="tax-row pl-6 text-sm">
              <div className="tax-label text-black">Monthly Contribution:</div>
              <div className="tax-value">{formatCurrency(incomeTaxCalculation.socialSecurity.monthlyContribution)}</div>
            </div>
            
            {incomeTaxCalculation.socialSecurity.solidarityContribution > 0 && (
              <div className="tax-row pl-6 text-sm">
                <div className="tax-label text-black">Solidarity Contribution:</div>
                <div className="tax-value">{formatCurrency(incomeTaxCalculation.socialSecurity.solidarityContribution)}</div>
              </div>
            )}
            
            <div className="tax-row tax-divider">
              <div className="tax-label text-black font-medium">Total Tax & Contributions:</div>
              <div className="tax-value text-black font-medium">{formatCurrency(incomeTaxCalculation.totalTaxLiability)}</div>
            </div>
            
            <div className="tax-row" style={{ marginTop: "8px" }}>
              <div className="tax-label text-black font-medium">Take Home Pay:</div>
              <div className="tax-value text-black font-medium">{formatCurrency(incomeTaxCalculation.takeHomePay)}</div>
            </div>
            
            <div className="tax-row" style={{ marginTop: "4px" }}>
              <div className="tax-label text-black text-xs">Effective Tax Rate:</div>
              <div className="tax-value text-black text-xs">{(incomeTaxCalculation.effectiveTaxRate * 100).toFixed(1)}%</div>
            </div>
            
            <div className="mt-3 text-xs text-black italic">
              Note: Spanish tax rates vary by autonomous community. This calculation uses the standard combined rates for 2025.
            </div>
          </div>
        </div>
      );
    }

    // Default tax calculation for other countries
    return (
      <div className="bg-accent-gradient p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-black mb-3">{taxJurisdiction} Income Tax</h3>
        
        <div>
          <div className="tax-row">
            <div className="tax-label text-black">Adjusted Net Profit:</div>
            <div className="tax-value font-medium">{formatCurrency(adjustedNetProfit)}</div>
          </div>

          {renderSelfEmployedCapitalAllowanceRows()}
          
          <div className="tax-row">
            <div className="tax-label text-black">Estimated Income Tax (25%):</div>
            <div className="tax-value font-medium">{formatCurrency(incomeTaxCalculation.incomeTax)}</div>
          </div>
          
          <div className="tax-row tax-divider">
            <div className="tax-label text-black font-medium">Total Tax:</div>
            <div className="tax-value text-black font-medium">{formatCurrency(incomeTaxCalculation.totalTaxLiability)}</div>
          </div>
          
          <div className="tax-row" style={{ marginTop: "8px" }}>
            <div className="tax-label text-black font-medium">Take Home Pay:</div>
            <div className="tax-value text-black font-medium">{formatCurrency(incomeTaxCalculation.takeHomePay)}</div>
          </div>
          
          <div className="tax-row" style={{ marginTop: "4px" }}>
            <div className="tax-label text-black text-xs">Effective Tax Rate:</div>
            <div className="tax-value text-black text-xs">{(incomeTaxCalculation.effectiveTaxRate * 100).toFixed(1)}%</div>
          </div>
          
          <div className="mt-3 text-xs text-black italic">
            Note: This is a simplified tax calculation. Please consult with a tax professional for accurate tax advice for {taxJurisdiction}.
          </div>
        </div>
      </div>
    );
  };

  // Render CGT section based on jurisdiction
  const renderCGTSection = () => {
    return (
      <div className="tax-section capital-gains bg-accent-gradient p-4 rounded-lg text-black">
        <h3 className="text-lg font-semibold text-black">
          {taxJurisdiction === "United States" ? "US Capital Gains Tax" :
           taxJurisdiction === "United Kingdom" ? "UK Capital Gains Tax" :
           taxJurisdiction === "Ireland" ? "Irish Capital Gains Tax" :
           `${taxJurisdiction} Capital Gains Tax`}
        </h3>
        
        <div>
          <div className="tax-row">
            <div className="tax-label text-black">Total Capital Gains:</div>
            <div className="tax-value font-medium text-black">{formatCurrency(cgtCalculation.totalGains)}</div>
          </div>
          
          <div className="tax-row">
            <div className="tax-label text-black">{cgtCalculation.cgtRateLabel}:</div>
            <div className="tax-value font-medium text-black">{formatCurrency(cgtCalculation.taxDue)}</div>
          </div>
          
          <div className="mt-3 text-xs text-black">
            Note: This is a simplified calculation. Actual CGT may vary based on holding periods, exemptions, and other factors.
          </div>
        </div>
      </div>
    );
  };

  const renderRctSection = () => {
    const tracker = rctTracker && typeof rctTracker === 'object' ? rctTracker : {};
    const supplierSummaries = Array.isArray(tracker.supplierSummaries) ? tracker.supplierSummaries : [];
    const principalRows = Array.isArray(tracker.principalRows) ? tracker.principalRows : [];
    const subcontractorRows = Array.isArray(tracker.subcontractorRows) ? tracker.subcontractorRows : [];
    const paymentRows = Array.isArray(tracker.paymentRows) ? tracker.paymentRows : [];
    const summaryCards = [
      { label: 'Subcontractor invoices', value: tracker.principalGrossInvoices || 0 },
      { label: 'Net payable to suppliers', value: tracker.principalNetPayable || 0 },
      { label: 'RCT deducted', value: tracker.principalRctDue || 0 },
      { label: 'Submitted to Revenue', value: tracker.rctPaidToRevenue || 0 },
      { label: 'RCT liability unpaid', value: tracker.rctBalanceDue || 0 },
      { label: 'RCT credits received', value: tracker.subcontractorRctCredits || 0 }
    ];
    const detailSections = [
      { id: 'supplier-balances', label: 'Supplier RCT balances' },
      { id: 'recent-liabilities', label: 'Recent RCT liabilities' },
      { id: 'subcontracting-income', label: 'Subcontracting income with RCT deducted' },
      { id: 'revenue-payments', label: 'Recent RCT payments to Revenue' }
    ];

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {summaryCards.map((card) => (
            <div key={card.label} className="rounded-lg border border-gray-200 bg-white p-3">
              <div className="text-xs uppercase tracking-wide text-gray-500">{card.label}</div>
              <div className="mt-1 text-lg font-semibold text-black">{formatCurrency(card.value)}</div>
            </div>
          ))}
        </div>

        {(tracker.subcontractorRctCreditsOutstanding || 0) > 0.01 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            RCT credits expected but not yet recorded: {formatCurrency(tracker.subcontractorRctCreditsOutstanding)}
          </div>
        )}

        <select
          value={selectedRctSection}
          onChange={(event) => setSelectedRctSection(event.target.value)}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-3 text-base shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          aria-label="Select RCT section"
        >
          <option value="">Select RCT section</option>
          {detailSections.map((section) => (
            <option key={section.id} value={section.id}>
              {section.label}
            </option>
          ))}
        </select>

        {selectedRctSection === 'supplier-balances' && (
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-3 py-2 text-sm font-semibold text-gray-800">Supplier RCT balances</div>
            {supplierSummaries.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-500">No RCT subcontractor invoices recorded.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {supplierSummaries.map((supplier) => (
                  <div key={supplier.id || supplier.name} className="grid grid-cols-1 gap-2 px-3 py-3 text-sm md:grid-cols-[1fr,110px,110px,110px,110px] md:items-center">
                    <div className="min-w-0">
                      <div className="truncate font-medium text-gray-900">{supplier.name || 'Supplier'}</div>
                      <div className="text-xs text-gray-500">Gross invoices {formatCurrency(supplier.grossInvoices || 0)}</div>
                    </div>
                    <div className="text-gray-700 md:text-right">Net due {formatCurrency(supplier.netOutstanding || 0)}</div>
                    <div className="text-gray-700 md:text-right">Net paid {formatCurrency(supplier.paidToSupplier || 0)}</div>
                    <div className="text-gray-700 md:text-right">RCT paid {formatCurrency(supplier.rctPaidToRevenue || 0)}</div>
                    <div className="font-semibold text-gray-900 md:text-right">RCT due {formatCurrency(supplier.rctOutstanding || 0)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {selectedRctSection === 'recent-liabilities' && (
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-3 py-2 text-sm font-semibold text-gray-800">Recent RCT liabilities</div>
            {principalRows.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-500">No RCT liabilities recorded.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {principalRows.slice(0, 6).map((row) => (
                  <div key={row.id} className="grid grid-cols-[1fr,90px,90px] gap-2 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <div className="truncate font-medium text-gray-900">{row.name || 'RCT subcontractor'}</div>
                      <div className="truncate text-xs text-gray-500">{row.jobReference || row.date || ''}</div>
                    </div>
                    <div className="text-right text-gray-700">{formatCurrency(row.rctPaidToRevenue || 0)}</div>
                    <div className="text-right font-medium text-gray-900">{formatCurrency(row.rctOutstanding || row.due || 0)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {selectedRctSection === 'subcontracting-income' && (
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-3 py-2 text-sm font-semibold text-gray-800">Subcontracting income with RCT deducted</div>
            {subcontractorRows.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-500">No subcontractor RCT credits recorded.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {subcontractorRows.slice(0, 6).map((row) => (
                  <div key={row.id} className="grid grid-cols-1 gap-2 px-3 py-2 text-sm md:grid-cols-[1fr,90px,90px,90px,90px] md:items-center">
                    <div className="min-w-0">
                      <div className="truncate font-medium text-gray-900">{row.name || 'Principal contractor'}</div>
                      <div className="truncate text-xs text-gray-500">{row.jobReference || row.date || ''}</div>
                    </div>
                    <div className="text-gray-700 md:text-right">Invoiced {formatCurrency(row.gross || 0)}</div>
                    <div className="text-gray-700 md:text-right">Net received {formatCurrency(row.netReceived || 0)}</div>
                    <div className="text-gray-700 md:text-right">Net owed {formatCurrency(row.netOutstanding || 0)}</div>
                    <div className="font-medium text-gray-900 md:text-right">RCT deducted {formatCurrency(row.credit || 0)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {selectedRctSection === 'revenue-payments' && (
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-3 py-2 text-sm font-semibold text-gray-800">Recent RCT payments to Revenue</div>
            {paymentRows.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-500">No RCT payments to Revenue recorded.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {paymentRows.slice(0, 6).map((row) => (
                  <div key={row.id} className="grid grid-cols-[1fr,90px] gap-2 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <div className="truncate font-medium text-gray-900">{row.description || 'RCT payment to Revenue'}</div>
                      <div className="truncate text-xs text-gray-500">{row.date || ''}</div>
                    </div>
                    <div className="text-right font-medium text-gray-900">{formatCurrency(row.amount || 0)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  
  // Render VAT/Sales Tax section
  const renderVATSection = () => {
    // Using vatSettings from component level
    
    // For US, calculate estimated sales tax
    if (taxJurisdiction === "United States") {
      // Get sales tax rate for the selected state
      const selectedState = usState;
      const salesTaxRate = taxRates.income.salesTax[selectedState] || 0;
      
      // Estimate taxable sales (income from goods/services)
      const taxableSales = transactions
        .filter(t => t.type === 'income' && t.category !== 'assetSale')
        .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
      
      // Estimate taxable purchases (expenses that might include sales tax)
      const taxablePurchases = transactions
        .filter(t => (t.type === 'expense' || t.type === 'expenses') && !isCapitalExpense(t))
        .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
      
      // Estimate sales tax collected (assuming all sales are taxable)
      const salesTaxCollected = taxableSales * salesTaxRate;
      
      // Estimate sales tax paid (assuming all purchases include sales tax)
      // This is a simplification - in reality, not all purchases include sales tax
      const salesTaxPaid = taxablePurchases * salesTaxRate / (1 + salesTaxRate);
      
      // Net sales tax position
      const netSalesTaxPosition = salesTaxCollected - salesTaxPaid;
      
      return (
        <div className="tax-section sales-tax bg-purple-50 p-4 rounded-lg text-black">
          <h3 className="text-lg font-semibold text-black">
            Sales Tax Summary ({selectedState})
          </h3>
          
          <div>
            <div className="tax-row">
              <div className="tax-label text-black">Sales Tax Rate:</div>
              <div className="tax-value font-medium text-black">{(salesTaxRate * 100).toFixed(2)}%</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black">Estimated Sales Tax Collected:</div>
              <div className="tax-value font-medium text-black">{formatCurrency(salesTaxCollected)}</div>
            </div>
            
            <div className="tax-row">
              <div className="tax-label text-black">Estimated Sales Tax Paid:</div>
              <div className="tax-value font-medium text-black">{formatCurrency(salesTaxPaid)}</div>
            </div>
            
            <div className="tax-row tax-divider">
              <div className="tax-label text-black font-medium">Net Sales Tax:</div>
              <div className="tax-value font-medium text-black">
                {formatCurrency(netSalesTaxPosition)}
                {netSalesTaxPosition >= 0 ? ' (Due)' : ' (Refund)'}
              </div>
            </div>
            
            <div className="mt-3 text-xs text-black">
              Note: This is an estimate. Sales tax requirements vary by state and locality.
              Some purchases may be exempt from sales tax.
            </div>
          </div>
        </div>
      );
    }
    
    // For non-US countries, show current outstanding VAT after filed periods are applied
    const vatCollected = vatOutstandingForCurrentView.vatCollected;
    const vatPaid = vatOutstandingForCurrentView.vatPaid;
    const rawNetVatPosition = vatOutstandingForCurrentView.rawNetVat;
    const filedNet = vatOutstandingForCurrentView.filedNet;
    const laterFiledCarryForwardNet = vatOutstandingForCurrentView.laterFiledCarryForwardNet;
    const netVatPosition = vatOutstandingForCurrentView.netOutstanding;
    
    return (
      <div className="tax-section sales-tax bg-accent-gradient p-4 rounded-lg text-black">
        <h3 className="text-lg font-semibold text-black">
          {taxName} Summary
        </h3>
        
        <div>
          <div className="tax-row">
            <div className="tax-label text-black">
              {taxName} Collected:
            </div>
            <div className="tax-value font-medium text-black">{formatCurrency(vatCollected)}</div>
          </div>
          
          <div className="tax-row">
            <div className="tax-label text-black">
              {taxName} Paid:
            </div>
            <div className="tax-value font-medium text-black">{formatCurrency(vatPaid)}</div>
          </div>
          
          <div className="tax-row tax-divider">
            <div className="tax-label text-black font-medium">
              {taxJurisdiction === "United States" ? `Net ${taxName}:` : `Raw Net ${taxName}:`}
            </div>
            <div className="tax-value font-medium text-black">
              {formatCurrency(rawNetVatPosition)}
              {rawNetVatPosition >= 0 ? ' (Due)' : ' (Refund)'}
            </div>
          </div>

          <div className="tax-row">
            <div className="tax-label text-black">Already filed in this view:</div>
            <div className="tax-value font-medium text-black">{formatCurrency(filedNet)}</div>
          </div>

          {Number(laterFiledCarryForwardNet || 0) !== 0 && (
            <div className="tax-row">
              <div className="tax-label text-black">Already claimed in filed returns:</div>
              <div className="tax-value font-medium text-black">{formatCurrency(laterFiledCarryForwardNet)}</div>
            </div>
          )}

          <div className="tax-row tax-divider">
            <div className="tax-label text-black font-medium">Current Net {taxName}:</div>
            <div className="tax-value font-medium text-black">
              {formatCurrency(netVatPosition)}
              {netVatPosition >= 0 ? ' (Due)' : ' (Refund)'}
            </div>
          </div>
          
          <div className="mt-3 text-xs text-black">
            {taxJurisdiction === "United States" 
              ? "Note: Sales tax requirements vary by state and locality."
              : taxJurisdiction === "United Kingdom"
                ? "Note: UK VAT returns are usually filed every 3 months through HMRC MTD-compatible software."
                : "Note: VAT returns are typically filed bi-monthly, every 4 months, or every 6 months depending on your Revenue schedule."}
          </div>
        </div>
      </div>
    );
  };
  
  const parseDateValue = React.useCallback((value) => parseFlexibleDateValue(value), []);

  const formatDateForInput = React.useCallback((value) => formatDateForInputValue(value), []);

  const getCurrentVatPeriodRange = React.useCallback(() => {
    const now = new Date();
    if (taxJurisdiction === 'United Kingdom') {
      // Use calendar quarter as default UK VAT period (users can still override manually).
      const periodStartMonth = Math.floor(now.getMonth() / 3) * 3;
      const start = new Date(now.getFullYear(), periodStartMonth, 1);
      const end = new Date(now.getFullYear(), periodStartMonth + 3, 0);
      return {
        startDate: formatDateForInput(start),
        endDate: formatDateForInput(end)
      };
    }

    const periodStartMonth = now.getMonth() % 2 === 0 ? now.getMonth() : now.getMonth() - 1;
    const start = new Date(now.getFullYear(), periodStartMonth, 1);
    const end = new Date(now.getFullYear(), periodStartMonth + 2, 0);
    return {
      startDate: formatDateForInput(start),
      endDate: formatDateForInput(end)
    };
  }, [formatDateForInput, taxJurisdiction]);

  const getTransactionDateValue = React.useCallback((transaction) => {
    if (!transaction || typeof transaction !== 'object') return null;
    const candidates = [
      transaction.date,
      transaction.createdAt,
      transaction.updatedAt,
      transaction.timestamp
    ];
    for (const candidate of candidates) {
      const parsed = parseDateValue(candidate);
      if (parsed) return parsed;
    }
    return null;
  }, [parseDateValue]);

  const getTransactionUpdatedDateValue = React.useCallback((transaction) => {
    if (!transaction || typeof transaction !== 'object') return null;
    const candidates = [
      transaction.updatedAt,
      transaction.createdAt,
      transaction.timestamp,
      transaction.date
    ];
    for (const candidate of candidates) {
      const parsed = parseDateValue(candidate);
      if (parsed) return parsed;
    }
    return null;
  }, [parseDateValue]);

  const getVatReportingDateValue = React.useCallback((transaction) => {
    if (!transaction || typeof transaction !== 'object') return null;
    const txType = String(transaction.type || '').toLowerCase();
    const isVatTransaction = txType === 'income' || txType === 'expense' || txType === 'expenses';
    if (
      isVatTransaction &&
      vatSettings?.accountingBasis === 'invoice'
    ) {
      const invoiceDate = parseDateValue(
        transaction.invoiceDate ||
        transaction.billDate ||
        transaction.receiptDate ||
        transaction.date
      );
      if (invoiceDate) return invoiceDate;
    }
    return getTransactionDateValue(transaction);
  }, [getTransactionDateValue, parseDateValue, vatSettings?.accountingBasis]);

  const getVatTransactionKey = React.useCallback((transaction) => {
    if (!transaction || typeof transaction !== 'object') return '';
    if (transaction.id !== undefined && transaction.id !== null && String(transaction.id).trim() !== '') {
      return `id:${String(transaction.id).trim()}`;
    }

    const txDate = getTransactionDateValue(transaction);
    const txDateKey = txDate ? txDate.toISOString() : String(transaction.date || '');
    const txType = String(transaction.type || '').toLowerCase();
    const txAmount = Number(transaction.amount || 0).toFixed(2);
    const txCategory = String(transaction.category || '');
    const txDescription = String(transaction.description || '');
    const txCreatedAt = formatDateForInput(transaction.createdAt) || '';
    const txUpdatedAt = formatDateForInput(transaction.updatedAt) || '';

    return `anon:${txType}|${txAmount}|${txDateKey}|${txCategory}|${txDescription}|${txCreatedAt}|${txUpdatedAt}`;
  }, [formatDateForInput, getTransactionDateValue]);

  const getSignedTransactionVatAmount = React.useCallback((transaction) => {
    const boxAmounts = getTransactionVatBoxAmounts(transaction);
    return parseFloat(((Number(boxAmounts.vatCollected) || 0) - (Number(boxAmounts.vatPaid) || 0)).toFixed(2));
  }, [getTransactionVatBoxAmounts]);

  const [localVatSettings, setLocalVatSettings] = React.useState(vatSettings);
  const [showVatAdvancedSettings, setShowVatAdvancedSettings] = React.useState(false);
  const [vatReportPeriod, setVatReportPeriod] = React.useState(() => getCurrentVatPeriodRange());
  const [vatFiledReturnDraft, setVatFiledReturnDraft] = React.useState({
    filedVatCollected: '',
    filedVatPaid: '',
    filedNetVat: ''
  });

  useEffect(() => {
    setLocalVatSettings(vatSettings);
  }, [vatSettings]);

  useEffect(() => {
    setVatReportPeriod(getCurrentVatPeriodRange());
  }, [getCurrentVatPeriodRange]);

  // Save VAT settings when changed
  const handleVatSettingsChange = React.useCallback((newSettings) => {
    setLocalVatSettings(newSettings);
    if (syncVatSettings) {
      syncVatSettings(newSettings);
    }
  }, [syncVatSettings]);

  const getNormalizedVatRegistrationPeriods = React.useCallback((settings = localVatSettings) => {
    const periods = Array.isArray(settings?.registrationPeriods)
      ? settings.registrationPeriods
          .map((period) => ({
            id: String(period?.id || `vat-period-${period?.startDate || Date.now()}`),
            startDate: formatDateForInput(period?.startDate),
            endDate: formatDateForInput(period?.endDate)
          }))
          .filter((period) => period.startDate)
      : [];

    if (!periods.length && settings?.registrationDate && settings?.isRegistered !== false) {
      const startDate = formatDateForInput(settings.registrationDate);
      if (startDate) {
        periods.push({
          id: `vat-period-${startDate}-open`,
          startDate,
          endDate: ''
        });
      }
    }

    return periods.sort((a, b) => a.startDate.localeCompare(b.startDate));
  }, [formatDateForInput, localVatSettings]);

  const vatRegistrationPeriods = React.useMemo(
    () => getNormalizedVatRegistrationPeriods(localVatSettings),
    [getNormalizedVatRegistrationPeriods, localVatSettings]
  );

  const updateVatRegistrationPeriods = React.useCallback((nextPeriods) => {
    const normalizedPeriods = getNormalizedVatRegistrationPeriods({
      ...localVatSettings,
      registrationPeriods: nextPeriods
    });
    const hasOpenPeriod = normalizedPeriods.some((period) => !period.endDate);
    handleVatSettingsChange({
      ...localVatSettings,
      enabled: true,
      isRegistered: hasOpenPeriod,
      registrationDate: normalizedPeriods[0]?.startDate || '',
      registrationPeriods: normalizedPeriods
    });
  }, [getNormalizedVatRegistrationPeriods, handleVatSettingsChange, localVatSettings]);

  const handleVatPeriodFieldChange = React.useCallback((periodId, field, value) => {
    updateVatRegistrationPeriods(
      vatRegistrationPeriods.map((period) =>
        period.id === periodId
          ? {
              ...period,
              [field]: value
            }
          : period
      )
    );
  }, [updateVatRegistrationPeriods, vatRegistrationPeriods]);

  const handleRegisterForVat = React.useCallback(() => {
    const today = formatDateForInput(new Date());
    if (vatRegistrationPeriods.some((period) => !period.endDate)) return;

    updateVatRegistrationPeriods([
      ...vatRegistrationPeriods,
      {
        id: `vat-period-${Date.now()}`,
        startDate: today,
        endDate: ''
      }
    ]);
  }, [formatDateForInput, updateVatRegistrationPeriods, vatRegistrationPeriods]);

  const handleDeregisterFromVat = React.useCallback(() => {
    const today = formatDateForInput(new Date());
    const openPeriod = [...vatRegistrationPeriods].reverse().find((period) => !period.endDate);
    if (!openPeriod) return;

    updateVatRegistrationPeriods(
      vatRegistrationPeriods.map((period) =>
        period.id === openPeriod.id
          ? {
              ...period,
              endDate: today < period.startDate ? period.startDate : today
            }
          : period
      )
    );
  }, [formatDateForInput, updateVatRegistrationPeriods, vatRegistrationPeriods]);

  const handleClearVatRegistration = React.useCallback(() => {
    handleVatSettingsChange({
      ...localVatSettings,
      isRegistered: false,
      registrationDate: '',
      registrationPeriods: []
    });
  }, [handleVatSettingsChange, localVatSettings]);

  const formatVatRateInput = (rate) => {
    const parsedRate = Number(rate);
    if (!Number.isFinite(parsedRate) || parsedRate < 0) return '';
    const percent = (parsedRate > 1 ? parsedRate : parsedRate * 100);
    return Number.isInteger(percent) ? String(percent) : String(parseFloat(percent.toFixed(3)));
  };

  const handleCategoryVatRateChange = (categoryId, value) => {
    const parsedPercent = Number(value);
    if (!categoryId || !Number.isFinite(parsedPercent) || parsedPercent < 0) return;

    handleVatSettingsChange({
      ...localVatSettings,
      categoryVatRates: {
        ...(localVatSettings.categoryVatRates || {}),
        [categoryId]: parsedPercent / 100
      }
    });
  };

  const handleResetCategoryVatRate = (categoryId) => {
    if (!categoryId) return;
    const nextRates = { ...(localVatSettings.categoryVatRates || {}) };
    delete nextRates[categoryId];
    handleVatSettingsChange({
      ...localVatSettings,
      categoryVatRates: nextRates
    });
  };

  const parseVatFiledAmountInput = React.useCallback((value) => {
    const normalized = String(value ?? '').replace(/,/g, '.').trim();
    if (!normalized) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parseFloat(parsed.toFixed(2)) : null;
  }, []);

  const formatVatFiledAmountInput = React.useCallback((value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : '';
  }, []);

  const buildVatFiledAmountPayload = React.useCallback((draft) => {
    const filedVatCollected = parseVatFiledAmountInput(draft?.filedVatCollected);
    const filedVatPaid = parseVatFiledAmountInput(draft?.filedVatPaid);
    const filedNetVatFromInput = parseVatFiledAmountInput(draft?.filedNetVat);
    const filedNetVat = filedNetVatFromInput !== null
      ? filedNetVatFromInput
      : (filedVatCollected !== null || filedVatPaid !== null
          ? parseFloat(((filedVatCollected || 0) - (filedVatPaid || 0)).toFixed(2))
          : null);

    return {
      ...(filedVatCollected !== null ? { filedVatCollected } : {}),
      ...(filedVatPaid !== null ? { filedVatPaid } : {}),
      ...(filedNetVat !== null ? { filedNetVat } : {})
    };
  }, [parseVatFiledAmountInput]);

  const vatSourceTransactions = React.useMemo(() => {
    if (Array.isArray(allTransactions) && allTransactions.length > 0) {
      return allTransactions;
    }
    return Array.isArray(transactions) ? transactions : [];
  }, [allTransactions, transactions]);

  const vatFiledPeriods = React.useMemo(
    () => sanitizeVatFiledPeriods(localVatSettings?.vatFiledPeriods),
    [localVatSettings?.vatFiledPeriods]
  );

  const vatFiledSnapshotMap = React.useMemo(() => {
    return buildLatestVatFiledSnapshotMap(vatFiledPeriods);
  }, [vatFiledPeriods]);

  const vatOutstandingForCurrentView = React.useMemo(() => {
    const safeTransactions = Array.isArray(transactions) ? transactions : [];

    let vatCollected = 0;
    let vatPaid = 0;
    safeTransactions.forEach((transaction) => {
      const boxAmounts = getTransactionVatBoxAmounts(transaction);
      vatCollected += Number(boxAmounts.vatCollected) || 0;
      vatPaid += Number(boxAmounts.vatPaid) || 0;
    });

    vatCollected = parseFloat(vatCollected.toFixed(2));
    vatPaid = parseFloat(vatPaid.toFixed(2));
    const rawNetVat = parseFloat((vatCollected - vatPaid).toFixed(2));

    const filedNet = calculateVatFiledNetForTransactions({
      transactions: safeTransactions,
      vatFiledPeriods,
      vatFiledSnapshotMap,
      getVatTransactionKey,
      getSignedTransactionVatAmount
    });

    const selectedKeys = new Set(
      safeTransactions
        .map((transaction) => getVatTransactionKey(transaction))
        .filter(Boolean)
    );
    const filedPeriodIdsInView = new Set();

    vatFiledPeriods.forEach((period) => {
      const snapshotKeys = (Array.isArray(period?.transactionSnapshots) ? period.transactionSnapshots : [])
        .map((snapshot) => String(snapshot?.key || '').trim())
        .filter((key) => (
          key &&
          !key.startsWith('filed-return:') &&
          !key.startsWith('filed-period-outstanding:')
        ));

      if (snapshotKeys.length > 0 && snapshotKeys.every((key) => selectedKeys.has(key))) {
        filedPeriodIdsInView.add(period.id);
      }
    });

    const laterFiledCarryForwardNet = vatFiledPeriods.reduce((sum, period) => {
      const snapshots = Array.isArray(period?.transactionSnapshots) ? period.transactionSnapshots : [];
      const snapshotNet = snapshots.reduce((snapshotSum, snapshot) => {
        const key = String(snapshot?.key || '');
        const sourcePeriodId = key.startsWith('filed-return:')
          ? key.replace(/^filed-return:/, '')
          : key.startsWith('filed-period-outstanding:')
            ? key.replace(/^filed-period-outstanding:/, '')
            : '';

        if (!sourcePeriodId || !filedPeriodIdsInView.has(sourcePeriodId)) {
          return snapshotSum;
        }

        return snapshotSum + (Number(snapshot?.signedVatAmount) || 0);
      }, 0);

      return sum + snapshotNet;
    }, 0);

    const netOutstanding = parseFloat((rawNetVat - filedNet - laterFiledCarryForwardNet).toFixed(2));

    return {
      vatCollected,
      vatPaid,
      rawNetVat,
      filedNet,
      laterFiledCarryForwardNet: parseFloat(laterFiledCarryForwardNet.toFixed(2)),
      netOutstanding
    };
  }, [
    getSignedTransactionVatAmount,
    getTransactionVatBoxAmounts,
    getVatTransactionKey,
    transactions,
    vatFiledPeriods,
    vatFiledSnapshotMap
  ]);

  const getTransactionsForDateRange = React.useCallback((sourceTransactions, startDate, endDate) => {
    if (!Array.isArray(sourceTransactions) || !startDate || !endDate) return [];

    const start = parseDateValue(startDate);
    const end = parseDateValue(endDate);
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];

    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    return sourceTransactions.filter((transaction) => {
      const txDate = getVatReportingDateValue(transaction);
      if (!txDate) return false;
      const txType = String(transaction.type || '').toLowerCase();
      if (txType !== 'income' && txType !== 'expense' && txType !== 'expenses') return false;
      return txDate >= start && txDate <= end;
    });
  }, [getVatReportingDateValue, parseDateValue]);

  const vatReportTransactions = React.useMemo(
    () => getTransactionsForDateRange(vatSourceTransactions, vatReportPeriod.startDate, vatReportPeriod.endDate),
    [getTransactionsForDateRange, vatReportPeriod.endDate, vatReportPeriod.startDate, vatSourceTransactions]
  );

  const vatRangeFromCurrentView = React.useMemo(() => {
    if (!Array.isArray(transactions) || transactions.length === 0) return null;

    let minDate = null;
    let maxDate = null;
    transactions.forEach((transaction) => {
      const txDate = getVatReportingDateValue(transaction);
      if (!txDate) return;
      if (!minDate || txDate < minDate) minDate = txDate;
      if (!maxDate || txDate > maxDate) maxDate = txDate;
    });

    if (!minDate || !maxDate) return null;
    return {
      startDate: formatDateForInput(minDate),
      endDate: formatDateForInput(maxDate)
    };
  }, [formatDateForInput, getVatReportingDateValue, transactions]);

  const vatReportSummary = React.useMemo(() => {
    let vatCollected = 0;
    let vatPaid = 0;

    vatReportTransactions.forEach((transaction) => {
      const boxAmounts = getTransactionVatBoxAmounts(transaction);
      vatCollected += Number(boxAmounts.vatCollected) || 0;
      vatPaid += Number(boxAmounts.vatPaid) || 0;
    });

    vatCollected = parseFloat(vatCollected.toFixed(2));
    vatPaid = parseFloat(vatPaid.toFixed(2));
    const rawNetVat = parseFloat((vatCollected - vatPaid).toFixed(2));

    return { vatCollected, vatPaid, rawNetVat };
  }, [getTransactionVatBoxAmounts, vatReportTransactions]);

  const currentVatPeriodId = React.useMemo(
    () => `${vatReportPeriod.startDate || ''}__${vatReportPeriod.endDate || ''}`,
    [vatReportPeriod.endDate, vatReportPeriod.startDate]
  );

  const currentFiledVatPeriod = React.useMemo(
    () => vatFiledPeriods.find((period) => period.id === currentVatPeriodId) || null,
    [currentVatPeriodId, vatFiledPeriods]
  );

  const vatFiledNetForReportPeriod = React.useMemo(() => {
    return calculateVatFiledNetForTransactions({
      transactions: vatReportTransactions,
      vatFiledPeriods,
      vatFiledSnapshotMap,
      getVatTransactionKey,
      getSignedTransactionVatAmount
    });
  }, [
    getSignedTransactionVatAmount,
    getVatTransactionKey,
    vatFiledPeriods,
    vatFiledSnapshotMap,
    vatReportTransactions
  ]);

  const transactionVatCarryForwardAdjustments = React.useMemo(() => (
    buildVatCarryForwardAdjustments({
      transactions: vatSourceTransactions,
      startDate: vatReportPeriod.startDate,
      endDate: vatReportPeriod.endDate,
      vatFiledPeriods,
      vatFiledSnapshotMap,
      getVatTransactionKey,
      getSignedTransactionVatAmount,
      getTransactionDateValue: getVatReportingDateValue,
      getTransactionUpdatedDateValue
    })
  ), [
    getSignedTransactionVatAmount,
    getVatReportingDateValue,
    getTransactionUpdatedDateValue,
    getVatTransactionKey,
    vatFiledPeriods,
    vatFiledSnapshotMap,
    vatReportPeriod.endDate,
    vatReportPeriod.startDate,
    vatSourceTransactions
  ]);

  const filedReturnVatCarryForwardAdjustments = React.useMemo(() => (
    buildVatFiledReturnAdjustments({
      vatFiledPeriods,
      startDate: vatReportPeriod.startDate,
      endDate: vatReportPeriod.endDate
    })
  ), [vatFiledPeriods, vatReportPeriod.endDate, vatReportPeriod.startDate]);

  const filedPeriodOutstandingCarryForwardAdjustments = React.useMemo(() => {
    const getNextDay = (value) => {
      const parsed = parseDateValue(value);
      if (!parsed) return '';
      const next = new Date(parsed);
      next.setDate(next.getDate() + 1);
      return formatDateForInput(next);
    };

    const getFiledPeriodCarryDate = (sourcePeriod) => {
      const nextPeriodStart = getNextDay(sourcePeriod?.endDate || '');
      const isFollowedByFiledPeriod = vatFiledPeriods.some((period) => period.startDate === nextPeriodStart);
      return isFollowedByFiledPeriod ? '' : nextPeriodStart;
    };

    const rangeStart = parseDateValue(vatReportPeriod.startDate);
    const rangeEnd = parseDateValue(vatReportPeriod.endDate);
    if (!rangeStart || !rangeEnd) return [];
    rangeStart.setHours(0, 0, 0, 0);
    rangeEnd.setHours(23, 59, 59, 999);

    return vatFiledPeriods
      .map((period) => {
        const carryDate = getFiledPeriodCarryDate(period);
        const parsedCarryDate = parseDateValue(carryDate);
        if (!parsedCarryDate || parsedCarryDate < rangeStart || parsedCarryDate > rangeEnd) return null;

        const sourceTransactions = getTransactionsForDateRange(vatSourceTransactions, period.startDate, period.endDate);
        if (sourceTransactions.length === 0) return null;

        let sourceVatCollected = 0;
        let sourceVatPaid = 0;
        sourceTransactions.forEach((transaction) => {
          const boxAmounts = getTransactionVatBoxAmounts(transaction);
          sourceVatCollected += Number(boxAmounts.vatCollected) || 0;
          sourceVatPaid += Number(boxAmounts.vatPaid) || 0;
        });

        const sourceRawNetVat = parseFloat((sourceVatCollected - sourceVatPaid).toFixed(2));
        const filedNet = getVatFiledPeriodNetAmount(period, getVatFiledPeriodSnapshotNetAmount(period));
        const sourceTransactionAdjustments = buildVatCarryForwardAdjustments({
          transactions: vatSourceTransactions,
          startDate: period.startDate,
          endDate: period.endDate,
          vatFiledPeriods,
          vatFiledSnapshotMap,
          getVatTransactionKey,
          getSignedTransactionVatAmount,
          getTransactionDateValue: getVatReportingDateValue,
          getTransactionUpdatedDateValue
        });
        const sourceFiledReturnAdjustments = buildVatFiledReturnAdjustments({
          vatFiledPeriods,
          startDate: period.startDate,
          endDate: period.endDate
        });
        const sourceAdjustmentSummary = summarizeVatCarryForwardAdjustments([
          ...sourceTransactionAdjustments,
          ...sourceFiledReturnAdjustments
        ]);
        const filedAdjustmentNet = (Array.isArray(period.transactionSnapshots) ? period.transactionSnapshots : [])
          .reduce((sum, snapshot) => {
            const key = String(snapshot?.key || '');
            if (!key.startsWith('filed-return:') && !key.startsWith('filed-period-outstanding:')) {
              return sum;
            }
            return sum + (Number(snapshot?.signedVatAmount) || 0);
          }, 0);
        const sourceAdjustmentNet = parseFloat((
          Number(sourceAdjustmentSummary.netAdjustment || 0) +
          filedAdjustmentNet
        ).toFixed(2));
        const deltaSignedVatAmount = parseFloat((
          sourceRawNetVat -
          Number(filedNet || 0) +
          sourceAdjustmentNet
        ).toFixed(2));

        if (deltaSignedVatAmount === 0) return null;

        return {
          key: `filed-period-outstanding:${period.id}`,
          source: 'filed_period_outstanding',
          sourcePeriodId: period.id,
          type: 'vat_adjustment',
          category: 'filed_period',
          description: `Outstanding VAT from filed return ${period.startDate} to ${period.endDate}`,
          originalDate: period.endDate,
          adjustmentDate: carryDate,
          previousSignedVatAmount: Number(filedNet || 0),
          currentSignedVatAmount: parseFloat((sourceRawNetVat + sourceAdjustmentNet).toFixed(2)),
          deltaSignedVatAmount,
          previousVatAmount: parseFloat(Math.abs(Number(filedNet || 0)).toFixed(2)),
          currentVatAmount: parseFloat(Math.abs(sourceRawNetVat + sourceAdjustmentNet).toFixed(2)),
          deltaVatAmount: parseFloat(Math.abs(deltaSignedVatAmount).toFixed(2))
        };
      })
      .filter(Boolean);
  }, [
    formatDateForInput,
    getSignedTransactionVatAmount,
    getTransactionUpdatedDateValue,
    getTransactionVatBoxAmounts,
    getTransactionsForDateRange,
    getVatReportingDateValue,
    getVatTransactionKey,
    parseDateValue,
    vatFiledPeriods,
    vatFiledSnapshotMap,
    vatReportPeriod.endDate,
    vatReportPeriod.startDate,
    vatSourceTransactions
  ]);

  const filedPeriodOutstandingSourceIds = React.useMemo(
    () => new Set(filedPeriodOutstandingCarryForwardAdjustments.map((adjustment) => adjustment.sourcePeriodId)),
    [filedPeriodOutstandingCarryForwardAdjustments]
  );

  const filteredTransactionVatCarryForwardAdjustments = React.useMemo(() => {
    if (filedPeriodOutstandingSourceIds.size === 0) return transactionVatCarryForwardAdjustments;

    return transactionVatCarryForwardAdjustments.filter((adjustment) => {
      const originalDate = parseDateValue(adjustment.originalDate);
      if (!originalDate) return true;
      return !vatFiledPeriods.some((period) => {
        if (!filedPeriodOutstandingSourceIds.has(period.id)) return false;
        const periodStart = parseDateValue(period.startDate);
        const periodEnd = parseDateValue(period.endDate);
        if (!periodStart || !periodEnd) return false;
        periodStart.setHours(0, 0, 0, 0);
        periodEnd.setHours(23, 59, 59, 999);
        return originalDate >= periodStart && originalDate <= periodEnd;
      });
    });
  }, [filedPeriodOutstandingSourceIds, parseDateValue, transactionVatCarryForwardAdjustments, vatFiledPeriods]);

  const filteredFiledReturnVatCarryForwardAdjustments = React.useMemo(() => {
    if (filedPeriodOutstandingSourceIds.size === 0) return filedReturnVatCarryForwardAdjustments;
    return filedReturnVatCarryForwardAdjustments.filter((adjustment) => (
      !filedPeriodOutstandingSourceIds.has(String(adjustment?.key || '').replace(/^filed-return:/, ''))
    ));
  }, [filedPeriodOutstandingSourceIds, filedReturnVatCarryForwardAdjustments]);

  const vatCarryForwardAdjustments = React.useMemo(
    () => [
      ...filteredTransactionVatCarryForwardAdjustments,
      ...filteredFiledReturnVatCarryForwardAdjustments,
      ...filedPeriodOutstandingCarryForwardAdjustments
    ],
    [
      filedPeriodOutstandingCarryForwardAdjustments,
      filteredFiledReturnVatCarryForwardAdjustments,
      filteredTransactionVatCarryForwardAdjustments
    ]
  );

  const vatCarryForwardAdjustmentSummary = React.useMemo(
    () => summarizeVatCarryForwardAdjustments(vatCarryForwardAdjustments),
    [vatCarryForwardAdjustments]
  );

  const laterFiledCarryForwardNetForReportPeriod = React.useMemo(() => {
    if (!currentVatPeriodId || !vatReportPeriod.endDate) return 0;

    const currentPeriodEnd = parseDateValue(vatReportPeriod.endDate);
    if (!currentPeriodEnd) return 0;
    currentPeriodEnd.setHours(23, 59, 59, 999);

    const carryForwardKeys = new Set([
      `filed-period-outstanding:${currentVatPeriodId}`,
      `filed-return:${currentVatPeriodId}`
    ]);

    const laterFiledNet = vatFiledPeriods.reduce((sum, period) => {
      const periodStart = parseDateValue(period.startDate);
      if (!periodStart || periodStart <= currentPeriodEnd) return sum;

      const snapshotNet = (Array.isArray(period.transactionSnapshots) ? period.transactionSnapshots : [])
        .reduce((snapshotSum, snapshot) => (
          carryForwardKeys.has(String(snapshot?.key || ''))
            ? snapshotSum + (Number(snapshot?.signedVatAmount) || 0)
            : snapshotSum
        ), 0);

      return sum + snapshotNet;
    }, 0);

    return parseFloat(laterFiledNet.toFixed(2));
  }, [
    currentVatPeriodId,
    parseDateValue,
    vatFiledPeriods,
    vatReportPeriod.endDate
  ]);

  const vatReportOutstanding = React.useMemo(() => {
    const rawNetVat = Number(vatReportSummary.rawNetVat || 0);
    const filedNet = Number(vatFiledNetForReportPeriod || 0);
    const adjustmentNet = Number(vatCarryForwardAdjustmentSummary.netAdjustment || 0);
    const laterFiledCarryForwardNet = Number(laterFiledCarryForwardNetForReportPeriod || 0);
    const netOutstanding = parseFloat((rawNetVat - filedNet + adjustmentNet - laterFiledCarryForwardNet).toFixed(2));
    return {
      rawNetVat: parseFloat(rawNetVat.toFixed(2)),
      filedNet: parseFloat(filedNet.toFixed(2)),
      adjustmentNet: parseFloat(adjustmentNet.toFixed(2)),
      adjustmentCount: vatCarryForwardAdjustmentSummary.count || 0,
      laterFiledCarryForwardNet: parseFloat(laterFiledCarryForwardNet.toFixed(2)),
      netOutstanding,
      vatDue: parseFloat(Math.max(0, netOutstanding).toFixed(2)),
      vatReclaim: parseFloat(Math.max(0, -netOutstanding).toFixed(2))
    };
  }, [
    laterFiledCarryForwardNetForReportPeriod,
    vatCarryForwardAdjustmentSummary,
    vatFiledNetForReportPeriod,
    vatReportSummary.rawNetVat
  ]);

  const isCurrentVatPeriodFiled = React.useMemo(
    () => vatFiledPeriods.some((period) => period.id === currentVatPeriodId),
    [currentVatPeriodId, vatFiledPeriods]
  );

  React.useEffect(() => {
    if (currentFiledVatPeriod) {
      const fallbackNetVat = getVatFiledPeriodNetAmount(currentFiledVatPeriod, vatFiledNetForReportPeriod);
      setVatFiledReturnDraft({
        filedVatCollected: formatVatFiledAmountInput(currentFiledVatPeriod.filedVatCollected),
        filedVatPaid: formatVatFiledAmountInput(currentFiledVatPeriod.filedVatPaid),
        filedNetVat: formatVatFiledAmountInput(fallbackNetVat)
      });
      return;
    }

    const defaultNetVat = Number(vatReportSummary.rawNetVat || 0) +
      Number(vatCarryForwardAdjustmentSummary.netAdjustment || 0);
    setVatFiledReturnDraft({
      filedVatCollected: formatVatFiledAmountInput(vatReportSummary.vatCollected || 0),
      filedVatPaid: formatVatFiledAmountInput(vatReportSummary.vatPaid || 0),
      filedNetVat: formatVatFiledAmountInput(defaultNetVat)
    });
  }, [
    currentFiledVatPeriod,
    currentVatPeriodId,
    formatVatFiledAmountInput,
    vatCarryForwardAdjustmentSummary.netAdjustment,
    vatFiledNetForReportPeriod,
    vatReportSummary.rawNetVat,
    vatReportSummary.vatCollected,
    vatReportSummary.vatPaid
  ]);

  const updateCurrentFiledReturnAmounts = React.useCallback((nextDraft) => {
    const startDate = vatReportPeriod.startDate;
    const endDate = vatReportPeriod.endDate;
    if (!startDate || !endDate || !isCurrentVatPeriodFiled) return;

    const periodId = `${startDate}__${endDate}`;
    const existingPeriods = sanitizeVatFiledPeriods(localVatSettings?.vatFiledPeriods);
    const amountPayload = buildVatFiledAmountPayload(nextDraft);

    handleVatSettingsChange({
      ...localVatSettings,
      vatFiledPeriods: existingPeriods.map((period) => (
        period.id === periodId
          ? {
              ...period,
              filedVatCollected: amountPayload.filedVatCollected,
              filedVatPaid: amountPayload.filedVatPaid,
              filedNetVat: amountPayload.filedNetVat
            }
          : period
      ))
    });
  }, [
    buildVatFiledAmountPayload,
    handleVatSettingsChange,
    isCurrentVatPeriodFiled,
    localVatSettings,
    vatReportPeriod.endDate,
    vatReportPeriod.startDate
  ]);

  const handleVatFiledAmountChange = React.useCallback((field, value) => {
    setVatFiledReturnDraft((previousDraft) => {
      const nextDraft = { ...previousDraft, [field]: value };

      if (field === 'filedVatCollected' || field === 'filedVatPaid') {
        const filedVatCollected = parseVatFiledAmountInput(
          field === 'filedVatCollected' ? value : nextDraft.filedVatCollected
        );
        const filedVatPaid = parseVatFiledAmountInput(
          field === 'filedVatPaid' ? value : nextDraft.filedVatPaid
        );
        if (filedVatCollected !== null || filedVatPaid !== null) {
          nextDraft.filedNetVat = formatVatFiledAmountInput((filedVatCollected || 0) - (filedVatPaid || 0));
        }
      }

      return nextDraft;
    });
  }, [
    formatVatFiledAmountInput,
    parseVatFiledAmountInput
  ]);

  const handleVatFiledAmountBlur = React.useCallback(() => {
    updateCurrentFiledReturnAmounts(vatFiledReturnDraft);
  }, [updateCurrentFiledReturnAmounts, vatFiledReturnDraft]);

  const vatReportPeriodLabel = React.useMemo(() => {
    const start = parseDateValue(vatReportPeriod.startDate);
    const end = parseDateValue(vatReportPeriod.endDate);
    if (!start || !end) return 'No period selected';
    return `${formatDisplayDate(start, dateDisplayLocale)} - ${formatDisplayDate(end, dateDisplayLocale)}`;
  }, [dateDisplayLocale, parseDateValue, vatReportPeriod.endDate, vatReportPeriod.startDate]);

  const setVatRangeToCurrentView = React.useCallback(() => {
    if (!vatRangeFromCurrentView) return;
    setVatReportPeriod(vatRangeFromCurrentView);
  }, [vatRangeFromCurrentView]);

  const setVatRangeToCurrentPreset = React.useCallback(() => {
    setVatReportPeriod(getCurrentVatPeriodRange());
  }, [getCurrentVatPeriodRange]);

  const handleVatPeriodInputChange = React.useCallback((field, value) => {
    setVatReportPeriod((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleVatReturnFiledToggle = React.useCallback((isFiled) => {
    const startDate = vatReportPeriod.startDate;
    const endDate = vatReportPeriod.endDate;
    if (!startDate || !endDate) return;

    const periodId = `${startDate}__${endDate}`;
    const existingPeriods = sanitizeVatFiledPeriods(localVatSettings?.vatFiledPeriods);

    if (!isFiled) {
      handleVatSettingsChange({
        ...localVatSettings,
        vatFiledPeriods: existingPeriods.filter((period) => period.id !== periodId)
      });
      return;
    }

    const transactionSnapshotMap = new Map();

    vatReportTransactions.forEach((transaction) => {
      const key = getVatTransactionKey(transaction);
      if (!key) return;
      transactionSnapshotMap.set(key, {
        key,
        signedVatAmount: getSignedTransactionVatAmount(transaction)
      });
    });

    vatCarryForwardAdjustments.forEach((adjustment) => {
      if (!adjustment?.key) return;
      transactionSnapshotMap.set(adjustment.key, {
        key: adjustment.key,
        signedVatAmount: getVatCarryForwardAdjustmentSnapshotAmount(adjustment)
      });
    });

    const transactionSnapshots = Array.from(transactionSnapshotMap.values());
    const transactionKeys = transactionSnapshots.map((snapshot) => snapshot.key);
    const filedAmountPayload = buildVatFiledAmountPayload(vatFiledReturnDraft);

    handleVatSettingsChange({
      ...localVatSettings,
      vatFiledPeriods: [
        ...existingPeriods.filter((period) => period.id !== periodId),
        {
          id: periodId,
          startDate,
          endDate,
          filedAt: new Date().toISOString(),
          transactionKeys,
          transactionSnapshots,
          ...filedAmountPayload
        }
      ]
    });
  }, [
    buildVatFiledAmountPayload,
    vatCarryForwardAdjustments,
    getSignedTransactionVatAmount,
    getVatTransactionKey,
    handleVatSettingsChange,
    localVatSettings,
    vatFiledReturnDraft,
    vatReportPeriod.endDate,
    vatReportPeriod.startDate,
    vatReportTransactions
  ]);

  const renderVatManagementSection = () => {
    const netVat = Number(vatReportOutstanding.netOutstanding || 0);
    const statusClass = netVat > 0 ? 'text-red-600' : netVat < 0 ? 'text-emerald-600' : 'text-gray-700';
    const statusLabel = netVat > 0 ? `${taxName} to Pay` : netVat < 0 ? `${taxName} to Reclaim` : `${taxName} Balanced`;
    const statusAmount = Math.abs(netVat);

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h4 className="text-md font-semibold text-black">{taxName} Report</h4>
            <p className="text-xs text-gray-600">Selected period: {vatReportPeriodLabel}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={setVatRangeToCurrentPreset}
              className="px-3 py-1.5 text-xs rounded-full bg-gray-100 text-black hover:bg-gray-200"
            >
              {taxJurisdiction === 'United Kingdom' ? 'Current quarter' : 'Current bi-monthly'}
            </button>
            <button
              type="button"
              onClick={setVatRangeToCurrentView}
              className="px-3 py-1.5 text-xs rounded-full bg-gray-100 text-black hover:bg-gray-200"
              disabled={!vatRangeFromCurrentView}
            >
              Use current view
            </button>
          </div>
        </div>

        <p className="text-xs text-gray-700 bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2">
          This is an estimate based on saved transactions. Confirm with your accountant before filing.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-xs text-black">
            Period start
            <input
              type="date"
              value={vatReportPeriod.startDate || ''}
              onChange={(e) => handleVatPeriodInputChange('startDate', e.target.value)}
              className="mt-1 w-full px-3 py-2 border rounded-md text-sm"
            />
          </label>
          <label className="text-xs text-black">
            Period end
            <input
              type="date"
              value={vatReportPeriod.endDate || ''}
              onChange={(e) => handleVatPeriodInputChange('endDate', e.target.value)}
              className="mt-1 w-full px-3 py-2 border rounded-md text-sm"
            />
          </label>
        </div>

        <div className={`text-xl font-bold ${statusClass}`}>{formatCurrency(statusAmount)}</div>
        <div className={`text-xs font-semibold ${statusClass}`}>{statusLabel}</div>

        <div className="text-xs text-black space-y-1">
          <div className="flex justify-between">
            <span>{taxName} Collected (Sales):</span>
            <span>{formatCurrency(vatReportSummary.vatCollected || 0)}</span>
          </div>
          <div className="flex justify-between">
            <span>{taxName} Paid (Expenses):</span>
            <span>{formatCurrency(vatReportSummary.vatPaid || 0)}</span>
          </div>
          <div className="flex justify-between">
            <span>Raw {taxName} Position:</span>
            <span>{formatCurrency(vatReportSummary.rawNetVat || 0)}</span>
          </div>
          <div className="flex justify-between">
            <span>Already filed in this view:</span>
            <span>{formatCurrency(vatReportOutstanding.filedNet || 0)}</span>
          </div>
          <div className="flex justify-between">
            <span>Prior-period adjustments:</span>
            <span>{formatCurrency(vatReportOutstanding.adjustmentNet || 0)}</span>
          </div>
          {Number(vatReportOutstanding.laterFiledCarryForwardNet || 0) !== 0 && (
            <div className="flex justify-between">
              <span>Already claimed in later filed returns:</span>
              <span>{formatCurrency(vatReportOutstanding.laterFiledCarryForwardNet || 0)}</span>
            </div>
          )}
          <div className="flex justify-between font-medium border-t pt-1 mt-1">
            <span>Current {taxName} Position:</span>
            <span className={statusClass}>{formatCurrency(vatReportOutstanding.netOutstanding || 0)}</span>
          </div>
        </div>

        <div className="border border-gray-200 rounded-md p-3 bg-gray-50 space-y-2">
          <div className="text-xs font-semibold text-black">Filed return amounts</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="text-xs text-black">
              {taxName} on sales filed
              <input
                type="number"
                step="0.01"
                value={vatFiledReturnDraft.filedVatCollected}
                onChange={(e) => handleVatFiledAmountChange('filedVatCollected', e.target.value)}
                onBlur={handleVatFiledAmountBlur}
                className="mt-1 w-full px-3 py-2 border rounded-md text-sm"
              />
            </label>
            <label className="text-xs text-black">
              {taxName} on purchases filed
              <input
                type="number"
                step="0.01"
                value={vatFiledReturnDraft.filedVatPaid}
                onChange={(e) => handleVatFiledAmountChange('filedVatPaid', e.target.value)}
                onBlur={handleVatFiledAmountBlur}
                className="mt-1 w-full px-3 py-2 border rounded-md text-sm"
              />
            </label>
            <label className="text-xs text-black">
              Net {taxName} filed
              <input
                type="number"
                step="0.01"
                value={vatFiledReturnDraft.filedNetVat}
                onChange={(e) => handleVatFiledAmountChange('filedNetVat', e.target.value)}
                onBlur={handleVatFiledAmountBlur}
                className="mt-1 w-full px-3 py-2 border rounded-md text-sm"
              />
            </label>
          </div>
          <p className="text-xs text-gray-600">
            Enter the figures actually submitted. Any difference from the app snapshot is carried into the next {taxName} period.
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm text-black">
          <input
            type="checkbox"
            checked={isCurrentVatPeriodFiled}
            onChange={(e) => handleVatReturnFiledToggle(e.target.checked)}
            className="h-4 w-4"
          />
          Return filed for this period
        </label>
        <p className="text-xs text-gray-600">
          {taxJurisdiction === 'United Kingdom'
            ? `Untick if filed by mistake to restore this period in your HMRC outstanding ${taxName} view.`
            : `Untick if filed by mistake to restore the outstanding ${taxName} for that period.`}
        </p>
        {vatReportOutstanding.adjustmentCount > 0 && (
          <p className="text-xs text-gray-600">
            Includes {vatReportOutstanding.adjustmentCount} prior-period {taxName} correction{vatReportOutstanding.adjustmentCount === 1 ? '' : 's'} carried forward from filed returns.
          </p>
        )}

        <div className="pt-2 border-t border-gray-200">
          <ReportExport
            transactions={vatReportTransactions}
            vatAdjustments={vatCarryForwardAdjustments}
            categories={categories}
            buildTransactionVatEntries={buildTransactionVatEntries}
            selectedTimeframe="custom"
            startDate={vatReportPeriod.startDate}
            endDate={vatReportPeriod.endDate}
            user={user}
            handleGoogleSignIn={handleGoogleSignIn}
            isCapitalExpense={isCapitalExpense}
            reportTypes={['vat']}
            initialReportType="vat"
            dateLocale={dateDisplayLocale}
          />
        </div>
      </div>
    );
  };
  
  // Calculate totals for the tax year
  const yearlyTotals = React.useMemo(() => {
    // Get current tax year dates based on jurisdiction
    const now = new Date();
    const currentYear = now.getFullYear();
    
    let taxYearStart, taxYearEnd;
    
    if (taxJurisdiction === "United Kingdom") {
      // UK tax year: April 6 to April 5
      if (now.getMonth() < 3 || (now.getMonth() === 3 && now.getDate() < 6)) {
        // Before April 6, so current tax year started last year
        taxYearStart = new Date(currentYear - 1, 3, 6);
        taxYearEnd = new Date(currentYear, 3, 5);
      } else {
        // After April 6, so current tax year started this year
        taxYearStart = new Date(currentYear, 3, 6);
        taxYearEnd = new Date(currentYear + 1, 3, 5);
      }
    } else {
      // Most countries use calendar year
      taxYearStart = new Date(currentYear, 0, 1);
      taxYearEnd = new Date(currentYear, 11, 31);
    }
    
    // Format dates for display
    const formatDate = (date) => {
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    };
    
    // Debug log to check transactions
    console.log(`Tax calculation: Processing ${transactions.length} transactions for tax year ${currentYear}`);
    
    // Filter transactions for current tax year
    const taxYearTransactions = transactions.filter(t => {
      if (!t.date) {
        console.log('Transaction missing date:', t);
        return false;
      }
      
      const transactionDate = getTransactionDateValue(t);
      const isInTaxYear = transactionDate ? transactionDate >= taxYearStart && transactionDate <= taxYearEnd : false;
      
      // Debug log for transactions outside tax year
      if (!isInTaxYear) {
        console.log(`Transaction outside tax year: ${t.date}`, t);
      }
      
      return isInTaxYear;
    });
    
    console.log(`Tax calculation: Found ${taxYearTransactions.length} transactions in current tax year`);
    
    // Calculate income for tax year
    const taxYearIncome = taxYearTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
    
    // Calculate expenses for tax year - handle both 'expense' and 'expenses' types
    const taxYearExpenses = taxYearTransactions
      .filter(t => t.type === 'expense' || t.type === 'expenses')
      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
      
    console.log('Tax year transactions by type:', 
      taxYearTransactions.reduce((acc, t) => {
        acc[t.type] = (acc[t.type] || 0) + 1;
        return acc;
      }, {}));
    
    console.log(`Tax calculation: Income: ${taxYearIncome}, Expenses: ${taxYearExpenses}`);
    
    // Calculate profit for tax year
    const taxYearProfit = taxYearIncome - taxYearExpenses;
    
    return {
      taxYearLabel: taxJurisdiction === "United Kingdom" 
        ? `${taxYearStart.getFullYear()}/${taxYearEnd.getFullYear()}`
        : taxYearStart.getFullYear().toString(),
      taxYearStart: formatDate(taxYearStart),
      taxYearEnd: formatDate(taxYearEnd),
      income: taxYearIncome,
      expenses: taxYearExpenses,
      profit: taxYearProfit
    };
  }, [getTransactionDateValue, transactions, taxJurisdiction]);
  
  return (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden ${className}`}>
      {/* Collapsible Header */}
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="sr-only">Tax Calculation ({countryName})</span>
        {isExpanded ? (<ChevronUp className="text-black w-5 h-5" />) : (<ChevronDown className="text-black w-5 h-5" />)}
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="p-4">
          {/* Collapsible tax sections */}
          {/* Collapsible tax sections */}
          <div className="tax-grid space-y-4 mt-4">
            <div className="bg-warn-gradient rounded-lg shadow-sm overflow-hidden text-[color:var(--color-text)]">
              <button
                type="button"
                onClick={() => setShowIncomeTax(!showIncomeTax)}
                className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-semibold text-[color:var(--color-text)]"
              >
                <span>{isLimitedCompany ? 'Corporation Tax' : 'Income Tax'}</span>
                <span className="text-[color:var(--color-text)]">{showIncomeTax ? '^' : 'v'}</span>
              </button>
              {showIncomeTax && (
                <div className="p-4 bg-warn-gradient space-y-3">
                  <p className="text-xs text-gray-700 bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2">
                    This is an estimate based on saved transactions. Confirm with your accountant before filing.
                  </p>
                  {renderIncomeTaxSection()}
                </div>
              )}
            </div>

            <div className="bg-warn-gradient rounded-lg shadow-sm overflow-hidden text-[color:var(--color-text)]">
              <button
                type="button"
                onClick={() => setShowCapitalGains(!showCapitalGains)}
                className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-semibold text-[color:var(--color-text)]"
              >
                <span>Capital Gains Tax</span>
                <span className="text-[color:var(--color-text)]">{showCapitalGains ? '^' : 'v'}</span>
              </button>
              {showCapitalGains && (
                <div className="p-4 bg-warn-gradient">
                  {renderCGTSection()}
                </div>
              )}
            </div>

            {supportsRct && (
            <div className="bg-warn-gradient rounded-lg shadow-sm overflow-hidden text-[color:var(--color-text)]">
              <button
                type="button"
                onClick={() => setShowRct(!showRct)}
                className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-semibold text-[color:var(--color-text)]"
              >
                <span>RCT</span>
                <span className="text-[color:var(--color-text)]">{showRct ? '^' : 'v'}</span>
              </button>
              {showRct && (
                <div className="p-4 bg-warn-gradient space-y-3">
                  {renderRctSection()}
                </div>
              )}
            </div>
            )}

            <div ref={vatSectionRef} className="bg-warn-gradient rounded-lg shadow-sm overflow-hidden text-[color:var(--color-text)]">
              <button
                type="button"
                onClick={() => setShowVat(!showVat)}
                className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-semibold text-[color:var(--color-text)]"
              >
                <span>{taxJurisdiction === "United States" ? taxName : `${taxName} Report`}</span>
                <span className="text-[color:var(--color-text)]">{showVat ? '^' : 'v'}</span>
              </button>
              {showVat && (
                <div className="p-4 bg-warn-gradient space-y-3">
                  {renderVATSection()}
                  {/* VAT Settings Section */}
                  <div className="tax-settings">
                    <h3 className="text-lg font-semibold text-black mb-4">
                      {taxName} Settings
                    </h3>
                    
                    <div className="checkbox-group">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="vatEnabled"
                          checked={localVatSettings.enabled}
                          onChange={(e) => handleVatSettingsChange({
                            ...localVatSettings,
                            enabled: e.target.checked
                          })}
                          className="h-5 w-5 text-black focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="vatEnabled" className="ml-3 block text-sm font-medium text-black">
                          Enable {taxName} calculations
                        </label>
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="vatRegistered"
                          checked={localVatSettings.isRegistered}
                          onChange={(e) => {
                            if (taxJurisdiction !== "United States") {
                              if (e.target.checked) {
                                handleRegisterForVat();
                              } else {
                                handleClearVatRegistration();
                              }
                              return;
                            }
                            handleVatSettingsChange({
                              ...localVatSettings,
                              isRegistered: e.target.checked
                            });
                          }}
                          className="h-5 w-5 text-black focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="vatRegistered" className="ml-3 block text-sm font-medium text-black">
                          {taxJurisdiction === "United States" 
                            ? `Business is registered for ${taxName} collection`
                            : `Business is ${taxName} registered`}
                        </label>
                      </div>
                      
                      {localVatSettings.enabled && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-md">
                          <p className="text-sm text-black leading-relaxed">
                            {localVatSettings.isRegistered 
                              ? (taxJurisdiction === "United States"
                                  ? `Your business will collect and report ${taxName}.`
                                  : `Your business will charge ${taxName} on sales and can reclaim ${taxName} on purchases.`)
                              : (taxJurisdiction === "United States"
                                  ? `Your business is not registered for ${taxName} collection.`
                                  : `Your business is not ${taxName} registered. You cannot charge ${taxName} or reclaim ${taxName}.`)}
                          </p>
                          {taxJurisdiction === "United Kingdom" && (
                            <p className="mt-2 text-xs text-black">
                              UK VAT registration is generally required once taxable turnover exceeds GBP 90,000 on a rolling 12-month basis.
                            </p>
                          )}
                        </div>
                      )}

                      {taxJurisdiction !== "United States" && localVatSettings.enabled && (
                        <div className="mt-3 rounded-md border border-gray-200 bg-white">
                          <button
                            type="button"
                            onClick={() => setShowVatAdvancedSettings((prev) => !prev)}
                            className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-gray-50"
                            aria-expanded={showVatAdvancedSettings}
                          >
                            <span>
                              <span className="block text-sm font-medium text-black">{taxName} registration and rates</span>
                              <span className="mt-0.5 block text-xs text-black">
                                {vatRegistrationPeriods.some((period) => !period.endDate) ? 'Current registration period active' : `No active ${taxName} registration period`} - {categoryVatRateRows.length} categories
                              </span>
                            </span>
                            {showVatAdvancedSettings ? (
                              <ChevronUp className="h-4 w-4 flex-shrink-0 text-gray-600" />
                            ) : (
                              <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-600" />
                            )}
                          </button>

                          {showVatAdvancedSettings && (
                            <div className="space-y-3 border-t border-gray-200 p-3">
                              <div>
                            <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                              <div>
                                <h4 className="text-sm font-medium text-black">{taxName} registration periods</h4>
                                <p className="mt-1 text-xs text-black">
                                  {taxName} applies only to transaction dates inside these periods.
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {vatRegistrationPeriods.some((period) => !period.endDate) ? (
                                  <button
                                    type="button"
                                    onClick={handleDeregisterFromVat}
                                    className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
                                  >
                                    De-register
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={handleRegisterForVat}
                                    className="rounded-md border border-green-300 bg-green-50 px-3 py-1.5 text-xs font-medium text-green-800 hover:bg-green-100"
                                  >
                                    Register again
                                  </button>
                                )}
                              </div>
                            </div>

                            {vatRegistrationPeriods.length === 0 ? (
                              <div className="rounded-md border border-dashed border-gray-300 p-3 text-xs text-black">
                                No {taxName} registration period recorded yet.
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {vatRegistrationPeriods.map((period, index) => (
                                  <div key={period.id} className="grid grid-cols-1 gap-2 rounded-md border border-gray-200 bg-gray-50 p-2 sm:grid-cols-[1fr,1fr,auto] sm:items-end">
                                    <label className="text-xs font-medium text-black">
                                      Registered from
                                      <input
                                        type="date"
                                        value={period.startDate || ''}
                                        onChange={(e) => handleVatPeriodFieldChange(period.id, 'startDate', e.target.value)}
                                        className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm text-black bg-white"
                                      />
                                    </label>
                                    <label className="text-xs font-medium text-black">
                                      De-registered on
                                      <input
                                        type="date"
                                        value={period.endDate || ''}
                                        min={period.startDate || undefined}
                                        onChange={(e) => handleVatPeriodFieldChange(period.id, 'endDate', e.target.value)}
                                        className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm text-black bg-white"
                                      />
                                    </label>
                                    <span className={`rounded px-2 py-1 text-xs font-medium ${period.endDate ? 'bg-gray-200 text-gray-700' : 'bg-green-100 text-green-800'}`}>
                                      {period.endDate ? `Period ${index + 1}` : 'Current'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                              <div>
                            <label htmlFor="vatSalesRate" className="block text-sm font-medium text-black mb-2">
                              Product sales {taxName} rate
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              id="vatSalesRate"
                              value={Number.isFinite(Number(localVatSettings.salesRate)) ? (Number(localVatSettings.salesRate) * 100) : ''}
                              onChange={(e) => {
                                const parsedRate = Number(e.target.value);
                                handleVatSettingsChange({
                                  ...localVatSettings,
                                  salesRate: Number.isFinite(parsedRate) ? parsedRate / 100 : localVatSettings.salesRate
                                });
                              }}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="mt-2 text-xs text-black">
                              Service categories use the reduced service rate unless a category has its own rule, such as hairdressing from 1 July 2026.
                            </p>
                          </div>

                              {Array.isArray(categoryVatRateRows) && categoryVatRateRows.length > 0 && (
                                <div>
                              <div className="mb-2 flex items-center justify-between gap-3">
                                <h4 className="text-sm font-medium text-black">Category {taxName} rates</h4>
                                <span className="text-xs text-black">{categoryVatRateRows.length} categories</span>
                              </div>
                              <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200">
                                <div className="grid grid-cols-[minmax(0,1fr)_96px_72px] gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-black">
                                  <span>Category</span>
                                  <span className="text-right">Rate %</span>
                                  <span className="text-right">Default</span>
                                </div>
                                {categoryVatRateRows.map((row) => (
                                  <div key={row.id} className="grid grid-cols-[minmax(0,1fr)_96px_72px] items-center gap-2 border-b border-gray-100 px-3 py-2 last:border-b-0">
                                    <div className="min-w-0">
                                      <div className="truncate text-sm text-black">{row.name}</div>
                                      {row.hasOverride && (
                                        <div className="text-[11px] text-blue-700">Custom rate</div>
                                      )}
                                    </div>
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="0.1"
                                      value={formatVatRateInput(row.rate)}
                                      onChange={(e) => handleCategoryVatRateChange(row.id, e.target.value)}
                                      className="w-full rounded border border-gray-300 px-2 py-1 text-right text-sm text-black"
                                      aria-label={`${row.name} ${taxName} rate`}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleResetCategoryVatRate(row.id)}
                                      className="text-right text-xs text-blue-700 hover:underline disabled:text-gray-400 disabled:no-underline"
                                      disabled={!row.hasOverride}
                                    >
                                      Reset
                                    </button>
                                  </div>
                                ))}
                              </div>
                              <p className="mt-2 text-xs text-black">
                                These defaults come from the selected industry and service/product categories. Transaction VAT can still be edited directly on each transaction.
                              </p>
                                </div>
                              )}

                              <div>
                            <label htmlFor="vatAccountingBasis" className="block text-sm font-medium text-black mb-2">
                              {taxName} accounting basis
                            </label>
                            <select
                              id="vatAccountingBasis"
                              value={localVatSettings.accountingBasis || 'cash'}
                              onChange={(e) => handleVatSettingsChange({
                                ...localVatSettings,
                                accountingBasis: e.target.value === 'invoice' ? 'invoice' : 'cash'
                              })}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="cash">Cash basis - report when paid or received</option>
                              <option value="invoice">Invoice basis - report by invoice or receipt date</option>
                            </select>
                            <p className="mt-2 text-xs text-black">
                              This controls whether unpaid sales invoices and unpaid supplier bills appear in the {taxName} report before payment.
                            </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {taxJurisdiction !== "United States" && localVatSettings.enabled && localVatSettings.isRegistered && (
                    <div>
                      {renderVatManagementSection()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>


          {/* Asset Sales Detail Section moved to Capital Report */}
          
          {/* Tax Disclaimer */}
          <div className="mt-6 p-4 bg-accent-gradient rounded-lg">
            <h3 className="text-md font-medium text-[color:var(--color-text)] mb-2">Important Tax Disclaimer</h3>
            <p className="text-sm text-[color:var(--color-text)]">
              This tax calculation is for estimation purposes only and should not be used for filing taxes. 
              Please consult with a qualified tax professional for accurate tax advice specific to your situation.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaxCalculation;
